import useSSE from '../useSSE';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
const d1=(n)=>{const v=Math.round((Number(n)||0)*10)/10;return Number.isInteger(v)?String(v):v.toFixed(1);};


const STATUS_LABELS = {
  not_started:'Not Started', planning:'Planning', in_progress:'In Progress',
  escalated:'Escalated', at_risk:'At Risk', on_hold:'On Hold',
  completed:'Completed', cancelled:'Cancelled'
};
const STATUS_COLORS = {
  not_started:'#8A93A6', planning:'#2563EB', in_progress:'#5B5BD6',
  escalated:'#DC2626', at_risk:'#D97706', on_hold:'#8A93A6',
  completed:'#16A34A', cancelled:'#6B7280'
};
const PRIORITY_COLORS = { urgent:'#DC2626', high:'#D97706', normal:'#2563EB', low:'#8A93A6' };

// ── Helpers
function pColor(p) {
  return p>=100?'#16A34A':p>=60?'#5B5BD6':p>=30?'#D97706':'#DC2626';
}

function Avatar({ name, color, size=28 }) {
  const initials = (name||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  return <div style={{width:size,height:size,borderRadius:'50%',background:color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.34,fontWeight:'700',color:'#fff',flexShrink:0}}>{initials}</div>;
}

// ── Stat Card
function StatCard({ label, value, sub, color, icon }) {
  return (
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'18px 20px',boxShadow:'var(--shadow-sm)',display:'flex',flexDirection:'column',gap:'6px'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <span style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted)'}}>{label}</span>
        <div style={{width:32,height:32,borderRadius:'8px',background:(color||'#5B5BD6')+'15',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'15px'}}>{icon}</div>
      </div>
      <div style={{fontSize:'34px',fontWeight:'800',color:color||'var(--text)',lineHeight:1,letterSpacing:'-1.5px'}}>{value}</div>
      {sub&&<div style={{fontSize:'11px',color:'var(--muted)'}}>{sub}</div>}
    </div>
  );
}

// ── Donut Chart (SVG)
function DonutChart({ segments, size=130, label }) {
  // Reliable stroke-based donut ring — works for any value including 100% single segment
  const segsFiltered = segments.filter(s => Number(s.value||0) > 0);
  const total = segsFiltered.reduce((s,sg) => s + Number(sg.value||0), 0);
  
  if (total === 0) return (
    <div style={{width:size,height:size,borderRadius:'50%',background:'var(--surface3)',
      display:'flex',alignItems:'center',justifyContent:'center',
      fontSize:'11px',color:'var(--muted)',flexDirection:'column',gap:'4px'}}>
      <span style={{fontSize:'18px'}}>—</span>
      <span>No data</span>
    </div>
  );

  const cx = size/2, cy = size/2;
  const sw = Math.max(12, size * 0.15);  // stroke width = ring thickness
  const R  = size/2 - sw/2 - 4;         // radius at ring center
  const circ = 2 * Math.PI * R;
  const GAP  = segsFiltered.length > 1 ? 3 : 0; // gap between segments in px

  // Build segments with dash arrays
  let cumulativeAngle = -Math.PI / 2; // start at 12 o'clock
  const arcs = segsFiltered.map(seg => {
    const frac  = Number(seg.value) / total;
    const sweep = frac * circ - GAP;
    const startDeg = (cumulativeAngle * 180) / Math.PI;
    cumulativeAngle += frac * 2 * Math.PI;
    return {
      color: seg.color,
      dash: `${Math.max(0, sweep)} ${circ}`,
      rot:  startDeg + 90, // rotate() transform works in degrees
      label: seg.label,
      value: seg.value,
      pct:  Math.round(frac * 100),
    };
  });

  // Centre label — passed in or derived from completion
  const completePct = label || (Math.round(
    (segsFiltered.find(s=>s.label==='Completed')?.value||0) / total * 100
  ) + '%');

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{display:'block',overflow:'visible'}}>
      {/* Background track */}
      <circle cx={cx} cy={cy} r={R} fill="none"
        stroke="var(--surface3)" strokeWidth={sw}/>
      {/* Coloured arcs */}
      {arcs.map((arc, i) => (
        <circle key={i} cx={cx} cy={cy} r={R}
          fill="none"
          stroke={arc.color}
          strokeWidth={sw}
          strokeDasharray={arc.dash}
          strokeDashoffset={0}
          strokeLinecap="butt"
          transform={`rotate(${arc.rot} ${cx} ${cy})`}
          style={{transition:'stroke-dasharray .5s ease'}}
        />
      ))}
      {/* Centre text */}
      <text x={cx} y={cy - 6} textAnchor="middle"
        fontSize={size < 110 ? 14 : 18} fontWeight="800"
        fill="var(--text)">{completePct}</text>
      <text x={cx} y={cy + 10} textAnchor="middle"
        fontSize="9" fontWeight="600" fill="var(--muted)">
        {total} tasks
      </text>
    </svg>
  );
}

// ── Bar Chart
function MiniBarChart({ data, height=80 }) {
  const max = Math.max(...data.map(d=>d.value),1);
  return (
    <div style={{display:'flex',alignItems:'flex-end',gap:'5px',height,paddingTop:'16px'}}>
      {data.map((d,i)=>(
        <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'3px',height:'100%',justifyContent:'flex-end'}}>
          <span style={{fontSize:'9px',color:'var(--muted)',fontWeight:'600'}}>{d.value||''}</span>
          <div style={{width:'100%',background:d.color||'#5B5BD6',borderRadius:'4px 4px 0 0',height:max>0?`${(d.value/max)*100}%`:'2px',minHeight:'3px',transition:'height .4s ease'}}/>
          <span style={{fontSize:'9px',color:'var(--muted)',whiteSpace:'nowrap',overflow:'hidden',maxWidth:'100%',textAlign:'center',textOverflow:'ellipsis'}}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── PERT Chart (simplified visual)
function OpenTasksBars({ segs }) {
  if (!segs || segs.length === 0) return null;
  const maxV = Math.max.apply(null, segs.map(function(x){ return x.value; }).concat([1]));
  return (
    <div style={{display:'flex',flexDirection:'column',gap:'8px',overflowY:'auto',maxHeight:'200px'}}>
      {segs.map(function(s) {
        return (
          <div key={s.label}>
            <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'3px'}}>
              <div style={{width:7,height:7,borderRadius:'50%',background:s.color,flexShrink:0}}/>
              <span style={{fontSize:'11px',fontWeight:'600',color:'var(--text)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.label}</span>
              <div style={{display:'flex',gap:'4px',fontSize:'9px'}}>
                {s.in_progress>0 && <span style={{background:'#EEF2FF',color:'#5B5BD6',padding:'1px 5px',borderRadius:'99px',fontWeight:'700'}}>⚡{s.in_progress}</span>}
                {s.at_risk>0 && <span style={{background:'#FFFBEB',color:'#D97706',padding:'1px 5px',borderRadius:'99px',fontWeight:'700'}}>⚠️{s.at_risk}</span>}
                {s.escalated>0 && <span style={{background:'#FEF2F2',color:'#DC2626',padding:'1px 5px',borderRadius:'99px',fontWeight:'700'}}>🚨{s.escalated}</span>}
              </div>
              <span style={{fontSize:'12px',fontWeight:'800',color:s.color,minWidth:'20px',textAlign:'right'}}>{s.value}</span>
            </div>
            <div style={{height:6,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
              <div style={{height:'100%',width:((s.value/maxV)*100)+'%',background:s.color,borderRadius:'99px',transition:'width .5s'}}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusOverTimeChart({ data }) {
  const canvasRef = React.useRef(null);
  const chartRef = React.useRef(null);
  React.useEffect(() => {
    if (!canvasRef.current || !data || data.length === 0) return;
    if (typeof window.Chart === 'undefined') return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    const labels = data.map(d => {
      const dt = new Date(d.week_start + 'T00:00:00');
      return String(dt.getDate()).padStart(2,'0') + '/' + String(dt.getMonth()+1).padStart(2,'0');
    });
    chartRef.current = new window.Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [
          { label:'Completed',   data:data.map(d=>d.completed||0),   borderColor:'#16A34A', backgroundColor:'rgba(22,163,74,0.08)',  tension:.4, fill:true,  pointRadius:3, borderWidth:2 },
          { label:'In Progress', data:data.map(d=>d.in_progress||0), borderColor:'#5B5BD6', backgroundColor:'rgba(91,91,214,0.06)', tension:.4, fill:false, pointRadius:3, borderWidth:2 },
          { label:'Not Started', data:data.map(d=>d.not_started||0), borderColor:'#9CA3AF', backgroundColor:'transparent',           tension:.4, fill:false, pointRadius:3, borderWidth:1.5, borderDash:[4,3] },
          { label:'At Risk',     data:data.map(d=>d.at_risk||0),     borderColor:'#D97706', backgroundColor:'transparent',           tension:.4, fill:false, pointRadius:3, borderWidth:1.5 },
          { label:'Escalated',   data:data.map(d=>d.escalated||0),   borderColor:'#DC2626', backgroundColor:'transparent',           tension:.4, fill:false, pointRadius:3, borderWidth:1.5 },
        ]
      },
      options: {
        responsive:true, maintainAspectRatio:false,
        plugins:{legend:{display:false}, tooltip:{mode:'index',intersect:false}},
        scales:{
          x:{grid:{color:'rgba(0,0,0,0.04)'},ticks:{color:'#8A93A6',font:{size:10},maxRotation:0,autoSkip:true,maxTicksLimit:12}},
          y:{grid:{color:'rgba(0,0,0,0.04)'},ticks:{color:'#8A93A6',font:{size:10},stepSize:1,beginAtZero:true},min:0}
        },
        interaction:{mode:'index',intersect:false}
      }
    });
    return ()=>{ if(chartRef.current){chartRef.current.destroy();chartRef.current=null;} };
  }, [data]);

  React.useEffect(()=>{
    if(typeof window.Chart==='undefined'){
      const s=document.createElement('script');
      s.src='https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
      s.onload=()=>{ if(canvasRef.current&&data&&data.length>0){ canvasRef.current.dispatchEvent(new Event('chartjs-ready')); } };
      document.head.appendChild(s);
    }
  },[]);

  if (!data || data.length === 0) return null;
  return <div style={{position:'relative',height:'140px'}}><canvas ref={canvasRef}/></div>;
}

function PERTChart({ tasks, dependencies }) {
  const [exportMsg, setExportMsg] = React.useState('');
  if (!tasks?.length) return <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'40px 0'}}>No tasks found. Add tasks to see the PERT network.</div>;

  const deps = dependencies || [];
  const nodeW = 160, nodeH = 80, padX = 80, padY = 50;

  // ── Topological sort to assign columns (levels)
  const taskMap = {};
  tasks.forEach(t => { taskMap[t.id] = { ...t, level: 0, col: 0 }; });

  // Build adjacency: depends_on → task (predecessor → successor)
  const successors = {};  // id -> [ids that depend on this]
  const predecessors = {}; // id -> [ids this depends on]
  tasks.forEach(t => { successors[t.id] = []; predecessors[t.id] = []; });
  deps.forEach(d => {
    if (taskMap[d.task_id] && taskMap[d.depends_on_task_id]) {
      successors[d.depends_on_task_id].push(d.task_id);
      predecessors[d.task_id].push(d.depends_on_task_id);
    }
  });

  // Assign levels via longest path from roots
  const roots = tasks.filter(t => predecessors[t.id].length === 0);
  // Iterative level assignment using BFS to avoid stack overflow
  const queue = roots.map(t => ({ id: t.id, level: 0 }));
  const visiting = new Set();
  while (queue.length > 0) {
    const { id, level } = queue.shift();
    if (!taskMap[id]) continue;
    if (taskMap[id].level < level) taskMap[id].level = level;
    (successors[id] || []).forEach(sId => {
      if (!visiting.has(id + '-' + sId)) {
        visiting.add(id + '-' + sId);
        queue.push({ id: sId, level: taskMap[id].level + 1 });
      }
    });
  }

  // Group by level
  const levelGroups = {};
  tasks.forEach(t => {
    const lv = taskMap[t.id].level;
    if (!levelGroups[lv]) levelGroups[lv] = [];
    levelGroups[lv].push(t.id);
  });
  const numLevels = Math.max(...Object.keys(levelGroups).map(Number)) + 1;
  const maxInLevel = Math.max(...Object.values(levelGroups).map(g => g.length));

  // Assign x,y positions
  const W = numLevels * (nodeW + padX) + padX;
  const H = maxInLevel * (nodeH + padY) + padY;
  Object.entries(levelGroups).forEach(([lv, ids]) => {
    const totalH = ids.length * nodeH + (ids.length - 1) * padY;
    const startY = (H - totalH) / 2;
    ids.forEach((id, i) => {
      taskMap[id].x = parseInt(lv) * (nodeW + padX) + padX;
      taskMap[id].y = startY + i * (nodeH + padY);
    });
  });

  // ── Critical path: find longest chain by duration
  const criticalSet = new Set();
  function getDuration(id) {
    const t = taskMap[id];
    if (!t) return 0;
    if (t.start_date && t.due_date) {
      return (new Date(t.due_date) - new Date(t.start_date)) / 86400000;
    }
    return 1;
  }
  // Simple longest path
  const memo = {};
  // Iterative topological longest path
  const inDegree = {};
  tasks.forEach(t => { inDegree[t.id] = (predecessors[t.id]||[]).length; });
  const topoQueue = tasks.filter(t => inDegree[t.id] === 0).map(t => t.id);
  tasks.forEach(t => { memo[t.id] = getDuration(t.id); });
  const topoVisited = new Set();
  while (topoQueue.length > 0) {
    const id = topoQueue.shift();
    if (topoVisited.has(id)) continue;
    topoVisited.add(id);
    (successors[id] || []).forEach(sId => {
      const newDist = memo[id] + getDuration(sId);
      if (newDist > (memo[sId] || 0)) memo[sId] = newDist;
      inDegree[sId] = (inDegree[sId] || 1) - 1;
      if (inDegree[sId] <= 0 && !topoVisited.has(sId)) topoQueue.push(sId);
    });
  }
  // Trace critical path
  // Iterative critical path trace
  const maxRootMemo = roots.length > 0 ? Math.max(...roots.map(r => memo[r.id]||0)) : 0;
  const critQueue = roots.filter(t => (memo[t.id]||0) === maxRootMemo).map(t => t.id);
  const critVisited = new Set();
  while (critQueue.length > 0) {
    const id = critQueue.shift();
    if (critVisited.has(id)) continue;
    critVisited.add(id);
    criticalSet.add(id);
    const succs = successors[id] || [];
    if (!succs.length) continue;
    const best = succs.reduce((a,b) => (memo[a]||0) > (memo[b]||0) ? a : b);
    if (!critVisited.has(best)) critQueue.push(best);
  }

  function exportPDF() {
    const svgEl = document.getElementById('pert-svg');
    if (!svgEl) return;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([svgData], {type:'image/svg+xml'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'PERT_Chart.svg'; a.click();
    URL.revokeObjectURL(url);
    setExportMsg('Saved as SVG — open in browser and print as PDF');
    setTimeout(()=>setExportMsg(''),4000);
  }

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'12px',flexWrap:'wrap'}}>
        <div style={{display:'flex',gap:'16px',fontSize:'11px',alignItems:'center'}}>
          <span style={{display:'flex',alignItems:'center',gap:'4px'}}><div style={{width:12,height:3,background:'#DC2626',borderRadius:'2px'}}/> Critical Path</span>
          <span style={{display:'flex',alignItems:'center',gap:'4px'}}><div style={{width:12,height:3,background:'#CBD5E1',borderRadius:'2px'}}/> Dependency</span>
          {deps.length===0&&<span style={{color:'#D97706',fontWeight:'600'}}>⚠️ No dependencies set — add them via task edit → 🔗 Dependencies tab</span>}
        </div>
        <button onClick={exportPDF} style={{marginLeft:'auto',padding:'5px 12px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',cursor:'pointer',fontSize:'12px',fontWeight:'600',color:'var(--text)'}}>📋 Summary Report</button>
        {exportMsg&&<span style={{fontSize:'11px',color:'#16A34A'}}>{exportMsg}</span>}
      </div>
      <div style={{overflowX:'auto',overflowY:'auto',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',background:'#F8F9FC',minHeight:'400px',height:'calc(100vh - 280px)'}}>
        <svg id="pert-svg" width={Math.max(W, 800)} height={Math.max(H, 400)} style={{display:'block',minWidth:'100%',minHeight:'100%'}}>
          <defs>
            <marker id="arrow-dep" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
              <path d="M0 0 L8 3 L0 6 Z" fill="#CBD5E1"/>
            </marker>
            <marker id="arrow-crit" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
              <path d="M0 0 L8 3 L0 6 Z" fill="#DC2626"/>
            </marker>
          </defs>

          {/* Dependency arrows */}
          {deps.map((dep,i)=>{
            const from = taskMap[dep.depends_on_task_id];
            const to = taskMap[dep.task_id];
            if (!from||!to) return null;
            const isCrit = criticalSet.has(dep.depends_on_task_id)&&criticalSet.has(dep.task_id);
            const x1=from.x+nodeW, y1=from.y+nodeH/2;
            const x2=to.x, y2=to.y+nodeH/2;
            const cx1=x1+40, cx2=x2-40;
            return (
              <path key={i}
                d={`M${x1} ${y1} C${cx1} ${y1} ${cx2} ${y2} ${x2} ${y2}`}
                fill="none"
                stroke={isCrit?'#DC2626':'#CBD5E1'}
                strokeWidth={isCrit?2.5:1.5}
                strokeDasharray={isCrit?'none':'none'}
                markerEnd={isCrit?'url(#arrow-crit)':'url(#arrow-dep)'}
                opacity={isCrit?1:0.7}
              />
            );
          })}

          {/* Task nodes */}
          {tasks.map(t=>{
            const tm = taskMap[t.id];
            if (!tm) return null;
            const color = STATUS_COLORS[t.status]||'#8A93A6';
            const p = Math.round(t.calculatedProgress||t.progress||0);
            const isCrit = criticalSet.has(t.id);
            // Split long names into up to 2 lines
            const words = t.name.split(' ');
            let line1 = '', line2 = '';
            for (const w of words) {
              if ((line1+' '+w).trim().length <= 18) line1 = (line1+' '+w).trim();
              else line2 = (line2+' '+w).trim();
            }
            if (!line2 && t.name.length > 18) { line1 = t.name.slice(0,17)+'—'; line2 = t.name.slice(17); }
            if (line2.length > 20) line2 = line2.slice(0,19)+'…';
            return (
              <g key={t.id}>
                {/* Shadow */}
                <rect x={tm.x+2} y={tm.y+2} width={nodeW} height={nodeH} rx="8" fill="rgba(0,0,0,0.06)"/>
                {/* Node bg */}
                <rect x={tm.x} y={tm.y} width={nodeW} height={nodeH} rx="8" fill="white"
                  stroke={isCrit?'#DC2626':color} strokeWidth={isCrit?2.5:1.5}/>
                {/* Progress bar at bottom */}
                {p>0&&<rect x={tm.x+2} y={tm.y+nodeH-8} width={(nodeW-4)*(p/100)} height={6} rx="3" fill={color} opacity="0.5"/>}
                {/* Critical indicator */}
                {isCrit&&<rect x={tm.x} y={tm.y} width={5} height={nodeH} rx="4" fill="#DC2626"/>}
                {/* Task name */}
                {line2
  ? <>
      <text x={tm.x+(isCrit?10:nodeW/2)} y={tm.y+14} textAnchor={isCrit?"start":"middle"} fontSize="9" fontWeight="700" fill="#1A1D23">{line1}</text>
      <text x={tm.x+(isCrit?10:nodeW/2)} y={tm.y+25} textAnchor={isCrit?"start":"middle"} fontSize="9" fontWeight="700" fill="#1A1D23">{line2}</text>
    </>
  : <text x={tm.x+(isCrit?10:nodeW/2)} y={tm.y+20} textAnchor={isCrit?"start":"middle"} fontSize="10" fontWeight="700" fill="#1A1D23">{line1}</text>
}
                {/* Dates */}
                <text x={tm.x+nodeW/2} y={tm.y+40} textAnchor="middle" fontSize="8" fill="#8A93A6">
                  {t.start_date?t.start_date.slice(5):'?'} → {t.due_date?t.due_date.slice(5):'?'}
                </text>
                {/* Progress % */}
                <text x={tm.x+nodeW/2} y={tm.y+57} textAnchor="middle" fontSize="9" fontWeight="700" fill={color}>{p}%</text>
              </g>
            );
          })}
        </svg>
      </div>
      <div style={{fontSize:'10px',color:'var(--muted)',marginTop:'8px',textAlign:'center'}}>
        {tasks.length} tasks · {deps.length} dependencies · {criticalSet.size>0?`Critical path: ${criticalSet.size} tasks`:"No critical path (add dependencies)"}
      </div>
    </div>
  );
}

// ── Permission Panel
function PermissionPanel({ projectId, allUsers, onClose }) {
  const [members, setMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPerm, setSelectedPerm] = useState('view');
  const [loading, setLoading] = useState(false);
  const [editingPerm, setEditingPerm] = useState({}); // userId -> new perm value

  useEffect(() => { loadMembers(); }, []);

  async function loadMembers() {
    try {
      const proj = await api.get(`/spaces/${projectId}`);
      setMembers(proj.members||[]);
    } catch(e) {}
  }

  async function grant() {
    if (!selectedUser) return;
    setLoading(true);
    try {
      await api.post(`/spaces/${projectId}/members`, { user_id: selectedUser, permission: selectedPerm });
      setSelectedUser('');
      await loadMembers();
    } catch(e) {}
    setLoading(false);
  }

  async function updatePerm(userId, newPerm) {
    try {
      await api.post(`/spaces/${projectId}/members`, { user_id: userId, permission: newPerm });
      setEditingPerm(p => { const n={...p}; delete n[userId]; return n; });
      await loadMembers();
    } catch(e) {}
  }

  async function removeMember(userId) {
    try {
      await api.delete(`/spaces/${projectId}/members/${userId}`);
      await loadMembers();
    } catch(e) {}
  }

  const PERM_COLORS = { view:'#2563EB', edit:'#D97706', manage:'#16A34A' };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{width:'520px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'20px',paddingBottom:'16px',borderBottom:'1px solid var(--border)'}}>
          <div style={{width:'36px',height:'36px',borderRadius:'8px',background:'rgba(91,91,214,0.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px'}}>🔒</div>
          <div>
            <h3 style={{fontSize:'15px',fontWeight:'700',margin:0}}>Project Permissions</h3>
            <p style={{fontSize:'11px',color:'var(--muted)',margin:0}}>Control who can access this project and at what level</p>
          </div>
        </div>

        {/* Grant access */}
        <div style={{background:'var(--surface2)',borderRadius:'var(--radius-lg)',padding:'14px',marginBottom:'16px'}}>
          <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted)',marginBottom:'10px'}}>Grant Access</div>
          <div style={{display:'flex',gap:'8px'}}>
            <select className="select" value={selectedUser} onChange={e=>setSelectedUser(e.target.value)} style={{flex:2}}>
              <option value="">Select team member…</option>
              {allUsers.filter(u=>!members.find(m=>m.id===u.id)).map(u=><option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
            <select className="select" value={selectedPerm} onChange={e=>setSelectedPerm(e.target.value)} style={{flex:1}}>
              <option value="view">View</option>
              <option value="edit">Edit</option>
              <option value="manage">Manage</option>
            </select>
            <button className="btn btn-primary btn-sm" onClick={grant} disabled={loading||!selectedUser}>Grant</button>
          </div>
          <div style={{fontSize:'10px',color:'var(--muted)',marginTop:'8px'}}>
            <strong>View</strong> — read only · <strong>Edit</strong> — create/edit tasks · <strong>Manage</strong> — full control
          </div>
        </div>

        {/* Current members */}
        <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted)',marginBottom:'10px'}}>Current Access ({members.length})</div>
        {members.length===0
          ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px',border:'1px dashed var(--border)',borderRadius:'var(--radius-lg)'}}>No members yet — grant access above</div>
          : <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              {members.map(m=>(
                <div key={m.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',background:'var(--surface2)',borderRadius:'var(--radius-lg)',border:'1px solid var(--border)'}}>
                  <Avatar name={m.full_name} color={m.avatar_color} size={32}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>{m.full_name}</div>
                    <div style={{fontSize:'11px',color:'var(--muted)'}}>{m.email}</div>
                  </div>
                  {/* Editable permission badge */}
                  <select
                    value={m.permission}
                    onChange={e=>updatePerm(m.id,e.target.value)}
                    style={{
                      fontSize:'11px',fontWeight:'700',padding:'3px 8px',borderRadius:'99px',
                      border:`1.5px solid ${PERM_COLORS[m.permission]||'#5B5BD6'}`,
                      background:`${PERM_COLORS[m.permission]||'#5B5BD6'}15`,
                      color:PERM_COLORS[m.permission]||'#5B5BD6',
                      cursor:'pointer',
                    }}>
                    <option value="view">View</option>
                    <option value="edit">Edit</option>
                    <option value="manage">Manage</option>
                  </select>
                  {/* Remove button */}
                  <button
                    onClick={()=>removeMember(m.id)}
                    title="Remove access"
                    style={{background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:'6px',padding:'4px 8px',cursor:'pointer',color:'#DC2626',fontSize:'11px',fontWeight:'600',transition:'all .15s'}}
                    onMouseEnter={e=>{e.currentTarget.style.background='rgba(220,38,38,0.15)';}}
                    onMouseLeave={e=>{e.currentTarget.style.background='rgba(220,38,38,0.08)';}}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
        }

        <div style={{display:'flex',justifyContent:'flex-end',marginTop:'20px',paddingTop:'16px',borderTop:'1px solid var(--border)'}}>
          <button className="btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ── Phase Permission Panel
function PhasePermissionPanel({ phaseId, allUsers, onClose }) {
  const [members, setMembers] = useState([]);
  const [phase, setPhase] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]); // members with access to the project
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPerm, setSelectedPerm] = useState('view');
  const [loading, setLoading] = useState(false);
  const PERM_COLORS = { view:'#2563EB', edit:'#D97706', manage:'#16A34A' };

  useEffect(()=>{ loadData(); },[phaseId]);

  async function loadData() {
    try {
      const [data, phases] = await Promise.all([
        api.get(`/phases/${phaseId}/members`).catch(()=>[]),
        api.get(`/phases/${phaseId}`).catch(()=>null),
      ]);
      setMembers(Array.isArray(data)?data:[]);
      setPhase(phases);
      // Only people who already have access to the parent project may be granted
      // phase access, so the picker is limited to project members.
      if (phases?.project_id) {
        try {
          const proj = await api.get(`/projects/${phases.project_id}`);
          setProjectMembers(Array.isArray(proj?.members) ? proj.members : []);
        } catch(e){ setProjectMembers([]); }
      }
    } catch(e){}
  }

  async function toggleVisibility() {
    const newVis = phase?.visibility==='private' ? 'inherited' : 'private';
    try {
      await api.put(`/phases/${phaseId}`,{visibility:newVis});
      // Optimistic UI update — show new state immediately
      setPhase(p=>({...p,visibility:newVis}));
      // If making private, add current user to members list immediately
      if (newVis === 'private') {
        await loadData(); // reload to get updated member list including auto-added creator
      }
      // Reload sidebar for this user
      if (ctx?.reloadSidebar && phase?.project_id) {
        ctx.reloadSidebar(phase.project_id);
      }
      // Dispatch window event so all pages reload silently
      window.dispatchEvent(new CustomEvent('trajectory:access_changed', {
        detail: { projectId: phase?.project_id, action: 'phase_visibility_changed' }
      }));
    } catch(e){ console.error('Toggle visibility failed:', e); }
  }

  async function grant() {
    if (!selectedUser) return;
    setLoading(true);
    try {
      await api.post(`/phases/${phaseId}/members`,{user_id:selectedUser,permission:selectedPerm});
      // Optimistically add the new member immediately (no wait for server roundtrip)
      const addedUser = projectMembers.find(u=>u.id===selectedUser) || allUsers.find(u=>u.id===selectedUser);
      if (addedUser) {
        setMembers(prev => {
          const exists = prev.find(m=>m.id===selectedUser);
          if (exists) return prev.map(m=>m.id===selectedUser?{...m,permission:selectedPerm}:m);
          return [...prev, {...addedUser, permission:selectedPerm}];
        });
      }
      if (ctx?.reloadSidebar && phase?.project_id) ctx.reloadSidebar(phase.project_id);
      setSelectedUser('');
      // Also do a background server refresh to ensure data is consistent
      loadData();
    } catch(e){ console.error('Add member failed:', e?.error||e); }
    setLoading(false);
  }

  async function updatePerm(userId,perm) {
    try {
      await api.post(`/phases/${phaseId}/members`,{user_id:userId,permission:perm});
      await loadData();
    } catch(e){}
  }

  async function removeMember(userId) {
    try {
      await api.delete(`/phases/${phaseId}/members/${userId}`);
      await loadData();
    } catch(e){}
  }

  const isPrivate = phase?.visibility === 'private';

  return (
    <div className="modal-overlay">
      <div className="modal" style={{width:'520px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'20px',paddingBottom:'14px',borderBottom:'1px solid var(--border)'}}>
          <div style={{width:'36px',height:'36px',borderRadius:'8px',background:'rgba(217,119,6,0.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px'}}>🔒</div>
          <div>
            <h3 style={{fontSize:'15px',fontWeight:'700',margin:0}}>Phase Permissions</h3>
            <p style={{fontSize:'11px',color:'var(--muted)',margin:0}}>Control who can see and access this phase</p>
          </div>
        </div>

        {/* Visibility toggle — the KEY feature */}
        <div style={{background:isPrivate?'rgba(220,38,38,0.05)':'rgba(22,163,74,0.05)',border:`1px solid ${isPrivate?'rgba(220,38,38,0.2)':'rgba(22,163,74,0.2)'}`,borderRadius:'var(--radius-lg)',padding:'14px 16px',marginBottom:'16px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div>
              <div style={{fontSize:'13px',fontWeight:'700',color:isPrivate?'#DC2626':'#16A34A',marginBottom:'3px'}}>
                {isPrivate ? '🔒 Private Phase' : '🌐 Inherited Access'}
              </div>
              <div style={{fontSize:'11px',color:'var(--muted)',lineHeight:1.5}}>
                {isPrivate
                  ? 'Only explicitly listed members below can see this phase. Project members cannot.'
                  : 'All project members can see this phase based on their project permission.'}
              </div>
            </div>
            <button onClick={toggleVisibility}
              style={{padding:'7px 14px',borderRadius:'var(--radius)',border:`1.5px solid ${isPrivate?'#DC2626':'#16A34A'}`,background:isPrivate?'rgba(220,38,38,0.08)':'rgba(22,163,74,0.08)',color:isPrivate?'#DC2626':'#16A34A',fontSize:'12px',fontWeight:'700',cursor:'pointer',flexShrink:0,marginLeft:'12px',transition:'all .15s'}}>
              {isPrivate ? 'Make Inherited' : 'Make Private'}
            </button>
          </div>
        </div>

        {/* Grant explicit access */}
        <div style={{background:'var(--surface2)',borderRadius:'var(--radius-lg)',padding:'12px',marginBottom:'16px'}}>
          <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted)',marginBottom:'10px'}}>
            {isPrivate ? 'Add Allowed Members' : 'Grant Override Access'}
          </div>
          <div style={{display:'flex',gap:'8px'}}>
            <select className="select" value={selectedUser} onChange={e=>setSelectedUser(e.target.value)} style={{flex:2}}>
              <option value="">Select team member…</option>
              {projectMembers.filter(u=>!members.find(m=>m.id===u.id)).map(u=><option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
            <select className="select" value={selectedPerm} onChange={e=>setSelectedPerm(e.target.value)} style={{flex:1}}>
              <option value="view">View</option>
              <option value="edit">Edit</option>
              <option value="manage">Manage</option>
            </select>
            <button className="btn btn-primary btn-sm" onClick={grant} disabled={loading||!selectedUser}>Add</button>
          </div>
        </div>

        {/* Member list */}
        <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted)',marginBottom:'10px'}}>
          {isPrivate ? `Allowed Members (${members.length})` : `Override Access (${members.length})`}
        </div>
        {members.length===0
          ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'16px',border:'1px dashed var(--border)',borderRadius:'var(--radius-lg)'}}>
              {isPrivate ? 'No members — phase is hidden from everyone except admins and owner.' : 'No overrides — all project members use project-level access.'}
            </div>
          : <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              {members.map(m=>(
                <div key={m.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',background:'var(--surface2)',borderRadius:'var(--radius-lg)',border:'1px solid var(--border)'}}>
                  <div style={{width:30,height:30,borderRadius:'50%',background:m.avatar_color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'700',color:'#fff',flexShrink:0}}>
                    {(m.full_name||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase()}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>{m.full_name}</div>
                    <div style={{fontSize:'11px',color:'var(--muted)'}}>{m.email}</div>
                  </div>
                  <select value={m.permission} onChange={e=>updatePerm(m.id,e.target.value)}
                    style={{fontSize:'11px',fontWeight:'700',padding:'3px 8px',borderRadius:'99px',border:`1.5px solid ${PERM_COLORS[m.permission]||'#5B5BD6'}`,background:`${PERM_COLORS[m.permission]||'#5B5BD6'}15`,color:PERM_COLORS[m.permission]||'#5B5BD6',cursor:'pointer'}}>
                    <option value="view">View</option>
                    <option value="edit">Edit</option>
                    <option value="manage">Manage</option>
                  </select>
                  <button onClick={()=>removeMember(m.id)}
                    style={{background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:'6px',padding:'4px 10px',cursor:'pointer',color:'#DC2626',fontSize:'11px',fontWeight:'600'}}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
        }

        <div style={{display:'flex',justifyContent:'flex-end',marginTop:'16px',paddingTop:'14px',borderTop:'1px solid var(--border)'}}>
          <button className="btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
// ── Main Dashboard
function ReportDropdown({ onExec, onSummary, onDetail, onFull, onWorkbook, onDeck, showWorkbook, onBoardReport }) {
  const [open, setOpen] = React.useState(false);
  const [boardOpen, setBoardOpen] = React.useState(false);
  const now = new Date();
  const [bMonth, setBMonth] = React.useState(now.getMonth() + 1);
  const [bYear, setBYear] = React.useState(now.getFullYear());
  const [bBusy, setBBusy] = React.useState('');
  const ref = React.useRef(null);
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const years = []; for (let y = now.getFullYear() + 1; y >= 2022; y--) years.push(y);

  React.useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setBoardOpen(false); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const runBoard = async (format) => {
    if (!onBoardReport) return;
    setBBusy(format);
    try { await onBoardReport(format, bMonth, bYear); }
    finally { setBBusy(''); }
  };
  const runDeck = async () => {
    if (!onDeck) return;
    setBBusy('deck');
    try { await onDeck(bMonth, bYear); }
    finally { setBBusy(''); }
  };

  const items = [
    { icon: '📊', label: 'Executive Report',     sub: 'High-level summary for management',    fn: onExec },
    { icon: '📋', label: 'Summary Report',        sub: 'Phase & list level, no task details',  fn: onSummary },
    { icon: '📄', label: 'Project Detail Report', sub: 'Tasks & timeline, no comments',        fn: onDetail },
    { icon: '📝', label: 'Full Report',           sub: 'Complete detail with all comments',    fn: onFull },
    ...(showWorkbook ? [{ icon: '📈', label: 'Project Workbook (Excel)', sub: 'Multi-sheet workbook: dashboard, WBS, Gantt, payments…', fn: onWorkbook }] : []),
  ];

  const selStyle = {flex:1,padding:'6px 8px',border:'1px solid var(--border)',borderRadius:'6px',fontSize:'11px',background:'var(--surface)',color:'var(--text)'};

  return (
    <div ref={ref} style={{position:'relative',display:'inline-block'}}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{padding:'6px 14px',background:'var(--brand)',border:'none',borderRadius:'var(--radius)',cursor:'pointer',fontSize:'11px',fontWeight:'700',color:'#fff',display:'flex',alignItems:'center',gap:'6px',userSelect:'none'}}>
        📋 Reports <span style={{fontSize:'10px',display:'inline-block',transform:open?'rotate(180deg)':'none',transition:'transform .15s'}}>▾</span>
      </button>
      {open && (
        <div style={{position:'absolute',right:0,top:'calc(100% + 6px)',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'10px',boxShadow:'0 8px 24px #0003',zIndex:999,minWidth:'270px',padding:'6px 0'}}>
          {(onBoardReport || (showWorkbook && onDeck)) && (
            <>
              <div style={{padding:'5px 14px 3px',fontSize:'8px',fontWeight:'800',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.6px'}}>Monthly Reports</div>
              <button onClick={() => setBoardOpen(o => !o)}
                style={{width:'100%',textAlign:'left',padding:'9px 14px',background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',gap:'2px'}}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <span style={{fontSize:'12px',fontWeight:'700',color:'var(--text)'}}>📅 Monthly Reports <span style={{fontSize:'9px',color:'var(--muted)'}}>{boardOpen?'▴':'▾'}</span></span>
                <span style={{fontSize:'9px',color:'var(--muted)'}}>Choose a month — Board Dashboard (Word/PDF) or Deck (PPT)</span>
              </button>
              {boardOpen && (
                <div style={{padding:'8px 14px 12px',borderBottom:'1px solid var(--border)'}} onClick={e=>e.stopPropagation()}>
                  <div style={{display:'flex',gap:'6px',marginBottom:'9px'}}>
                    <select value={bMonth} onChange={e=>setBMonth(parseInt(e.target.value))} style={selStyle}>
                      {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                    </select>
                    <select value={bYear} onChange={e=>setBYear(parseInt(e.target.value))} style={{...selStyle,flex:'0 0 78px'}}>
                      {years.map(y=><option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  {onBoardReport && (<>
                    <div style={{fontSize:'9px',fontWeight:'700',color:'var(--muted)',margin:'2px 0 5px'}}>Board Dashboard</div>
                    <div style={{display:'flex',gap:'6px',marginBottom:onDeck&&showWorkbook?'10px':'0'}}>
                      <button onClick={()=>runBoard('docx')} disabled={!!bBusy} style={{flex:1,padding:'7px',background:'var(--brand)',color:'#fff',border:'none',borderRadius:'6px',fontSize:'11px',fontWeight:'700',cursor:bBusy?'wait':'pointer'}}>{bBusy==='docx'?'…':'⬇ Word'}</button>
                      <button onClick={()=>runBoard('pdf')} disabled={!!bBusy} style={{flex:1,padding:'7px',background:'var(--surface)',color:'var(--brand)',border:'1px solid var(--brand)',borderRadius:'6px',fontSize:'11px',fontWeight:'700',cursor:bBusy?'wait':'pointer'}}>{bBusy==='pdf'?'…':'⬇ PDF'}</button>
                    </div>
                  </>)}
                  {showWorkbook && onDeck && (<>
                    <div style={{fontSize:'9px',fontWeight:'700',color:'var(--muted)',margin:'2px 0 5px'}}>PowerPoint Deck</div>
                    <button onClick={runDeck} disabled={!!bBusy} style={{width:'100%',padding:'7px',background:'var(--surface)',color:'var(--brand)',border:'1px solid var(--brand)',borderRadius:'6px',fontSize:'11px',fontWeight:'700',cursor:bBusy?'wait':'pointer'}}>{bBusy==='deck'?'Generating…':'⬇ Monthly Deck (.pptx)'}</button>
                  </>)}
                </div>
              )}
            </>
          )}
          <div style={{padding:'5px 14px 3px',fontSize:'8px',fontWeight:'800',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.6px'}}>Project Reports</div>
          {items.map(item => (
            <button key={item.label}
              onClick={() => { item.fn(); setOpen(false); }}
              style={{width:'100%',textAlign:'left',padding:'9px 14px',background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',gap:'2px'}}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <span style={{fontSize:'12px',fontWeight:'700',color:'var(--text)'}}>{item.icon} {item.label}</span>
              <span style={{fontSize:'9px',color:'var(--muted)'}}>{item.sub}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { spaceId } = useParams();
  const [toast, setToast] = useState(null);
  const showToast = (text) => { setToast(text); setTimeout(() => setToast(null), 5000); };

  // Real-time SSE — supplements the 8s poll for instant updates
  useSSE(spaceId, {
    task_updated:    () => { api.get('/spaces/'+spaceId+'/dashboard').then(fresh=>{ if(fresh) setData(prev=>({...prev,...fresh})); }).catch(()=>{}); },
    payment_updated: () => { api.get('/spaces/'+spaceId+'/dashboard').then(fresh=>{ if(fresh) setData(prev=>({...prev, paymentMilestones: fresh.paymentMilestones??prev?.paymentMilestones})); }).catch(()=>{}); },
    phase_updated:   () => { api.get('/spaces/'+spaceId+'/dashboard').then(fresh=>{ if(fresh) setData(prev=>({...prev,...fresh})); }).catch(()=>{}); },
    list_created:       () => { api.get('/spaces/'+spaceId+'/dashboard').then(fresh=>{ if(fresh) setData(prev=>({...prev,...fresh})); }).catch(()=>{}); },
    list_updated:       () => { api.get('/spaces/'+spaceId+'/dashboard').then(fresh=>{ if(fresh) setData(prev=>({...prev,...fresh})); }).catch(()=>{}); },
    timesheet_updated:  () => { api.get('/spaces/'+spaceId+'/dashboard').then(fresh=>{ if(fresh) setData(prev=>({...prev,...fresh})); }).catch(()=>{}); },
  });

  // Reload dashboard on global access changes
  React.useEffect(() => {
    function onAccessChanged(evt) {
      if (!evt.detail?.projectId || evt.detail.projectId === spaceId) {
        api.get('/spaces/'+spaceId+'/dashboard').then(fresh=>{ if(fresh) setData(prev=>({...prev,...fresh})); }).catch(()=>{});
      }
    }
    window.addEventListener('trajectory:access_changed', onAccessChanged);
    return () => window.removeEventListener('trajectory:access_changed', onAccessChanged);
  }, [spaceId]);
  const navigate = useNavigate();
  const ctx = useOutletContext();
  const isGuest = ctx?.isGuest || ctx?.user?.role === 'guest';
  const [data, setData] = useState(null);
  const [space, setSpace] = useState(null);
  // Permission flags — recalculate whenever space loads
  const myPermission = space?.userPermission || 'view';
  const canEdit   = myPermission === 'edit' || myPermission === 'manage';
  const canManage = myPermission === 'manage';
  const [folders, setFolders] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [showPermModal, setShowPermModal] = useState(false);
  const [phasePermModal, setPhasePermModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [weightAdjustModal, setWeightAdjustModal] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [newFolder, setNewFolder] = useState({ name:'', description:'', color:'#5B5BD6', visibility:'inherited' });
  const [newList, setNewList] = useState({ name:'', description:'', color:'#5B5BD6' });
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const setActiveTab = (tab) => setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
  const [showAllLists, setShowAllLists] = useState(false);
  // Financial Analysis tab visibility — only Financial PMs (or managers) see it.
  const [finAccess, setFinAccess] = useState(false);
  const [riskAccess, setRiskAccess] = useState(false);
  useEffect(() => {
    if (!spaceId) return;
    import('../utils/financialApi').then(m => m.default.get(`/projects/${spaceId}/access`)
      .then(r => setFinAccess(!!r.has_access)).catch(() => setFinAccess(false)));
    import('../utils/financialApi').then(m => m.default.get(`/projects/${spaceId}/risk-access`)
      .then(r => setRiskAccess(!!r.can_access)).catch(() => setRiskAccess(false)));
  }, [spaceId]);
  const [overviewPhaseFilter, setOverviewPhaseFilter] = useState('all'); // 'all' or phaseId
  const COLORS = ['#5B5BD6','#2563EB','#0891B2','#16A34A','#D97706','#DC2626','#EC4899','#7C3AED'];

  // Comprehensive Excel workbook — owner / PM / Super Admin only
  const isSuperAdmin = ctx?.user?.role === 'superadmin';
  const isOwnerOrPM = isSuperAdmin || !!data?.isProjectPM || (space?.owner_id && space.owner_id === ctx?.user?.id);
  const [showWeeklyModal, setShowWeeklyModal] = React.useState(false);
  const [weeklyCfg, setWeeklyCfg] = React.useState({ enabled:false, day:'1', time:'08:00', perMember:false });
  const [weeklyMsg, setWeeklyMsg] = React.useState('');
  const [weeklyBusy, setWeeklyBusy] = React.useState(false);
  React.useEffect(() => {
    if (showWeeklyModal && spaceId) {
      api.get(`/spaces/${spaceId}/weekly-summary`).then(c=>setWeeklyCfg({enabled:!!c.enabled,day:String(c.day??'1'),time:c.time||'08:00',perMember:!!c.perMember})).catch(()=>{});
    }
  }, [showWeeklyModal, spaceId]);
  async function saveWeekly() {
    setWeeklyBusy(true); setWeeklyMsg('');
    try { await api.put(`/spaces/${spaceId}/weekly-summary`, weeklyCfg); setWeeklyMsg('Saved.'); setTimeout(()=>setWeeklyMsg(''),3000); }
    catch(e){ setWeeklyMsg(e?.error||'Save failed.'); }
    finally { setWeeklyBusy(false); }
  }
  async function testWeekly() {
    setWeeklyBusy(true); setWeeklyMsg('');
    try { await api.put(`/spaces/${spaceId}/weekly-summary`, weeklyCfg); const r=await api.post(`/spaces/${spaceId}/weekly-summary/test`,{}); setWeeklyMsg(r?.message||'Test sent.'); }
    catch(e){ setWeeklyMsg(e?.error||'Test failed.'); }
    finally { setWeeklyBusy(false); setTimeout(()=>setWeeklyMsg(''),6000); }
  }
  async function exportProjectWorkbook() {
    try {
      const wbData = await api.get(`/spaces/${spaceId}/workbook-data`);
      const { buildProjectWorkbook } = await import('../utils/projectWorkbook');
      await buildProjectWorkbook(wbData);
    } catch (e) {
      showToast(e?.error || 'Could not generate the project workbook. You may not have access, or there was a problem gathering the data.');
    }
  }

  async function exportProjectDeck(month, year) {
    try {
      const now = new Date();
      const m = month || (now.getMonth() + 1);
      const y = year || now.getFullYear();
      const wbData = await api.get(`/spaces/${spaceId}/workbook-data`);
      // Pull the monthly board report data (financials, risks) for the deck's
      // Financial Dashboard + Key Risks slides. Tolerate absence (e.g. no financial access).
      let report = null;
      try {
        const token = localStorage.getItem('flowup_token');
        const r = await fetch(`/api/reports/projects/${spaceId}/report-data?year=${y}&month=${m}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) report = await r.json();
      } catch (e) { /* report data optional */ }
      const { buildProjectDeck } = await import('../utils/projectDeck');
      await buildProjectDeck(wbData, report);
    } catch (e) {
      showToast(e?.error || 'Could not generate the PowerPoint deck. You may not have access, or there was a problem gathering the data.');
    }
  }

  // Monthly Board Dashboard report (Word/PDF) — generated server-side.
  async function downloadBoardReport(format, month, year) {
    try {
      const token = localStorage.getItem('flowup_token');
      const res = await fetch(`/api/reports/projects/${spaceId}/report?year=${year}&month=${month}&format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let msg = 'Could not generate the report.';
        try { const j = await res.json(); msg = j.error || msg; } catch (e) { /* ignore */ }
        showToast(msg);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      a.download = `Board_Dashboard_${M[month - 1]}_${year}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      showToast('Could not generate the report.');
    }
  }

  function exportExecutiveReport() {
    const pw = window.open('','_blank','width=1200,height=900');
    if (!pw) return;
    const dash = data;
    const projectName = space?.name || dash?.spaceName || 'Project';
    if (!dash) return;
    const date = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'});
    const esc = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const ovProg  = Math.round((dash.overallProgress||0)*10)/10;
    const progCol = ovProg>=100?'#16A34A':ovProg>50?'#5B5BD6':ovProg>20?'#D97706':'#DC2626';
    const stats   = dash.stats||{};
    const totalLogged = (dash.listsProgress||[]).reduce((s,l)=>s+(l.loggedHours||0),0);
    const totalBudget = (dash.listsProgress||[]).reduce((s,l)=>s+(l.budgetHours||0),0);
    const hoursPct = totalBudget>0?Math.min(100,Math.round(totalLogged/totalBudget*100)):0;
    const hoursCol = hoursPct>100?'#DC2626':hoursPct>80?'#D97706':'#16A34A';

    // Arc gauge helpers (same as other reports)
    const arcS=(pct,col)=>{if(pct<=0)return'';const a=Math.PI*Math.min(pct/100,0.9999);return`<path d="M 30,68 A 50,50 0 0 1 ${(80-50*Math.cos(a)).toFixed(2)},${(68-50*Math.sin(a)).toFixed(2)}" fill="none" stroke="${col}" stroke-width="11" stroke-linecap="round"/>`;};
    const arcBgS=()=>'<path d="M 30,68 A 50,50 0 0 1 130,68" fill="none" stroke="#e8eaf0" stroke-width="11" stroke-linecap="round"/>';
    const gCard=(pct,col,sub)=>`<div style="text-align:center"><svg width="110" height="68" viewBox="0 0 160 90"><g>${arcBgS()}</g><g>${arcS(pct,col)}</g><text x="80" y="63" text-anchor="middle" font-size="24" font-weight="900" fill="${col}" font-family="Arial">${d1(pct)}%</text></svg>${sub?`<div style="font-size:8px;color:#aaa;margin-top:1px;font-style:italic">${esc(sub)}</div>`:''}</div>`;

    // Status badge
    const statusBadge = ovProg>=100
      ? '<span style="display:inline-block;padding:3px 10px;border-radius:20px;font-size:8px;font-weight:800;background:#DCFCE7;color:#16A34A;letter-spacing:.4px">● COMPLETED</span>'
      : ovProg>50
      ? '<span style="display:inline-block;padding:3px 10px;border-radius:20px;font-size:8px;font-weight:800;background:#DBEAFE;color:#1D4ED8;letter-spacing:.4px">● ON TRACK</span>'
      : ovProg>20
      ? '<span style="display:inline-block;padding:3px 10px;border-radius:20px;font-size:8px;font-weight:800;background:#FEF9C3;color:#D97706;letter-spacing:.4px">● IN PROGRESS</span>'
      : '<span style="display:inline-block;padding:3px 10px;border-radius:20px;font-size:8px;font-weight:800;background:#FEE2E2;color:#DC2626;letter-spacing:.4px">● NEEDS ATTENTION</span>';

    // Payment milestones (permission-gated)
    const pms      = dash.paymentMilestones||[];
    const pmTotal  = pms.reduce((s,p)=>s+parseFloat(p.amount||0),0);
    const pmRel    = pms.reduce((s,p)=>{const ar=(p.releaseLog||[]).reduce((rs,r)=>rs+parseFloat(r.amount||0),0);return s+(ar>0?ar:(p.status==='released'?parseFloat(p.amount||0):0));},0);
    const pmRelCnt = pms.filter(p=>p.status==='released').length;
    const pmRdyCnt = pms.filter(p=>p.status==='ready_for_release').length;
    const pmPndCnt = pms.filter(p=>p.status==='pending').length;
    const pmPct    = pmTotal>0?Math.round(pmRel/pmTotal*100):0;
    // Multi-currency grouping for report
    const pmByCur  = {};
    pms.forEach(p=>{const c=p.currency||'OMR';if(!pmByCur[c]){pmByCur[c]={total:0,released:0};}pmByCur[c].total+=parseFloat(p.amount||0);const _a=(p.releaseLog||[]).reduce((s,r)=>s+parseFloat(r.amount||0),0);pmByCur[c].released+=(_a>0?_a:(p.status==='released'?parseFloat(p.amount||0):0));});
    const pmCurrencies = Object.keys(pmByCur);
    const pmIsMulti = pmCurrencies.length > 1;
    const pmCurLine = pmCurrencies.map(c=>`${c} ${pmByCur[c].total.toLocaleString()} (${pmByCur[c].released.toLocaleString()} released)`).join(' &nbsp;|&nbsp; ');
    const pmFirstCur = pmCurrencies[0]||'OMR';

    let html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Executive Report — ${esc(projectName)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,Helvetica,sans-serif;background:#fff;color:#1a1a2e;font-size:10.5px;line-height:1.4;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  @page{size:A4 portrait;margin:8mm}
  .page{max-width:190mm;margin:0 auto;padding:6mm}
  @media print{.page{padding:0;max-width:100%}}
  /* All sections avoid breaking */
  .no-break{page-break-inside:avoid}
  /* Header */
  .hdr{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:9px;margin-bottom:10px;border-bottom:3px solid #5B5BD6}
  /* Cards */
  .card{background:#f8f9fc;border-radius:7px;border:1px solid #e8eaf0;padding:9px}
  .ct{font-size:7.5px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;color:#888;margin-bottom:5px}
  /* Grids */
  .r3{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:9px}
  .r2{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:9px}
  /* Progress bar */
  .pb{background:#e8eaf0;border-radius:4px;height:6px;overflow:hidden}
  .pf{height:100%;border-radius:4px}
  /* Table */
  table{width:100%;border-collapse:collapse;font-size:9.5px}
  th{background:#f1f3f8;padding:4px 6px;text-align:left;font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#888}
  td{padding:4px 6px;border-bottom:1px solid #e8eaf0;vertical-align:middle}
  tr:last-child td{border-bottom:none}
  /* Section label */
  .sec{font-size:7.5px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;color:#9CA3AF;margin-bottom:5px;display:flex;align-items:center;gap:5px}
  .sec::after{content:'';flex:1;height:1px;background:#F1F5F9}
  /* Footer */
  .ftr{display:flex;justify-content:space-between;font-size:7.5px;color:#aaa;border-top:1px solid #e8eaf0;padding-top:6px;margin-top:8px}
</style>
</head><body><div class="page">

<!-- HEADER -->
<div class="hdr no-break">
  <div>
    <div style="font-size:7.5px;color:#5B5BD6;font-weight:800;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px">Executive Summary Report &nbsp;·&nbsp; Confidential</div>
    <div style="font-size:20px;font-weight:900;color:#0F172A;letter-spacing:-.3px;line-height:1.1">${esc(projectName)}</div>
    <div style="font-size:8.5px;color:#888;margin-top:4px">Report Date: ${date} &nbsp;·&nbsp; Space Communication Technologies</div>
  </div>
  <div style="text-align:right;flex-shrink:0;margin-left:16px">
    <div style="font-size:32px;font-weight:900;color:${progCol};line-height:1">${d1(ovProg)}%</div>
    <div style="font-size:7.5px;color:#888;margin-top:2px;margin-bottom:5px">Overall Progress</div>
    ${statusBadge}
  </div>
</div>

<!-- GAUGES + TASK STATS -->
<div class="r3 no-break">
  <div class="card" style="text-align:center">${gCard(ovProg,progCol,'SPI-weighted')}<div class="ct" style="margin-top:4px;text-align:center">Overall Progress</div></div>
  <div class="card" style="text-align:center">${gCard(hoursPct,hoursCol,'of budget used')}<div class="ct" style="margin-top:4px;text-align:center">Hours Consumed</div></div>
  <div class="card" style="display:flex;flex-direction:column;justify-content:center;gap:6px">
    <div style="display:flex;justify-content:space-between;align-items:center"><span style="font-size:8.5px;color:#888">Total Tasks</span><span style="font-weight:900;font-size:13px">${stats.totalTasks||0}</span></div>
    <div style="display:flex;justify-content:space-between;align-items:center"><span style="font-size:8.5px;color:#888">Completed</span><span style="font-weight:900;font-size:13px;color:#16A34A">${stats.completedTasks||0}</span></div>
    <div style="display:flex;justify-content:space-between;align-items:center"><span style="font-size:8.5px;color:#888">In Progress</span><span style="font-weight:900;font-size:13px;color:#5B5BD6">${stats.inProgressTasks||0}</span></div>
    <div style="display:flex;justify-content:space-between;align-items:center"><span style="font-size:8.5px;color:#888">At Risk</span><span style="font-weight:900;font-size:13px;color:#F97316">${stats.atRiskTasks||0}</span></div>
    <div style="display:flex;justify-content:space-between;align-items:center"><span style="font-size:8.5px;color:#888">Overdue</span><span style="font-weight:900;font-size:13px;color:#DC2626">${stats.overdueTasks||0}</span></div>
    <div style="border-top:1px solid #e8eaf0;padding-top:5px;display:flex;justify-content:space-between;align-items:center"><span style="font-size:8.5px;color:#888">Logged / Budget</span><span style="font-weight:800;font-size:10px;color:#5B5BD6">${totalLogged.toFixed(1)}h / ${totalBudget>0?totalBudget.toFixed(1)+'h':'—'}</span></div>
  </div>
</div>`;

    // Phase cards — 2 columns, enriched
    html += '<div class="sec">Phase Status</div>';
    html += '<div class="r2 no-break">';
    (dash.phasesProgress||[]).forEach(ph => {
      const pp = Math.round(ph.progress||0);
      const pc = pp>=100?'#16A34A':pp>50?'#5B5BD6':pp>20?'#D97706':'#DC2626';
      const stBadge = pp>=100
        ? '<span style="font-size:7px;font-weight:800;padding:1px 7px;border-radius:10px;background:#DCFCE7;color:#16A34A">Done</span>'
        : pp>50
        ? '<span style="font-size:7px;font-weight:800;padding:1px 7px;border-radius:10px;background:#DBEAFE;color:#1D4ED8">Active</span>'
        : pp>0
        ? '<span style="font-size:7px;font-weight:800;padding:1px 7px;border-radius:10px;background:#FEF9C3;color:#D97706">Started</span>'
        : '<span style="font-size:7px;font-weight:800;padding:1px 7px;border-radius:10px;background:#F1F5F9;color:#6B7280">Pending</span>';
      const phLists   = (dash.listsProgress||[]).filter(l=>l.phase_id===ph.id||l.phase_name===ph.name);
      const phTasks   = phLists.reduce((s,l)=>s+(l.taskCount||0),0);
      const phLogged  = phLists.reduce((s,l)=>s+(l.loggedHours||0),0);
      const phBudget  = phLists.reduce((s,l)=>s+(l.budgetHours||0),0);
      const hpct      = phBudget>0?Math.min(100,Math.round(phLogged/phBudget*100)):0;
      const hcol      = phBudget>0&&phLogged>phBudget?'#DC2626':'#5B5BD6';
      html += '<div class="card no-break">'
        // Phase name + % + badge
        + '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:5px">'
        + '<div style="font-size:11px;font-weight:800;color:#0F172A">'+esc(ph.name)+'</div>'
        + '<div style="display:flex;align-items:center;gap:6px">'+stBadge+'<div style="font-size:16px;font-weight:900;color:'+pc+'">'+pp+'%</div></div>'
        + '</div>'
        // Progress bar
        + '<div class="pb" style="margin-bottom:7px"><div class="pf" style="width:'+pp+'%;background:'+pc+'"></div></div>'
        // 3 mini tiles
        + '<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:4px;margin-bottom:6px">'
        + '<div style="text-align:center;background:#f1f3f8;border-radius:4px;padding:4px 2px"><div style="font-weight:800;font-size:12px;color:#5B5BD6">'+phLists.length+'</div><div style="font-size:7.5px;color:#888">Lists</div></div>'
        + '<div style="text-align:center;background:#f1f3f8;border-radius:4px;padding:4px 2px"><div style="font-weight:800;font-size:12px;color:#374151">'+phTasks+'</div><div style="font-size:7.5px;color:#888">Tasks</div></div>'
        + '<div style="text-align:center;background:#f1f3f8;border-radius:4px;padding:4px 2px"><div style="font-weight:800;font-size:12px;color:#16A34A">'+phLogged.toFixed(1)+'h</div><div style="font-size:7.5px;color:#888">Logged</div></div>'
        + '</div>'
        // Budget bar
        + '<div style="font-size:7.5px;color:#888;margin-bottom:2px">Budget: '+phLogged.toFixed(1)+'h / '+(phBudget>0?phBudget.toFixed(1)+'h':'—')+'</div>'
        + '<div class="pb" style="margin-bottom:6px"><div class="pf" style="width:'+hpct+'%;background:'+hcol+'"></div></div>'
        // List table
        + (phLists.length>0
          ? '<table><thead><tr><th>List</th><th style="text-align:center">Tasks</th><th style="text-align:right">Progress</th></tr></thead><tbody>'
            + phLists.map(l=>{
                const lp=Math.round(l.progress||0);
                const lc=lp>=100?'#16A34A':lp>0?'#5B5BD6':'#9CA3AF';
                return '<tr>'
                  +'<td style="font-size:9px">'+esc(l.name)+'</td>'
                  +'<td style="text-align:center;font-size:9px">'+(l.taskCount||0)+'</td>'
                  +'<td style="text-align:right;font-weight:700;color:'+lc+';font-size:9px">'+lp+'%</td>'
                  +'</tr>';
              }).join('')
            + '</tbody></table>'
          : '')
        + '</div>';
    });
    html += '</div>';

    // Payment milestones (permission-gated)
    if (pms.length > 0) {
      html += '<div class="card no-break" style="margin-bottom:8px">'
        + '<div class="ct">Payment Milestones</div>'
        + '<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:8px">'
        + '<div style="text-align:center;background:#f1f3f8;border-radius:5px;padding:7px 4px"><div style="font-size:16px;font-weight:900;color:#5B5BD6">'+pms.length+'</div><div style="font-size:7.5px;color:#888;margin-top:1px">Total</div></div>'
        + '<div style="text-align:center;background:#DCFCE7;border-radius:5px;padding:7px 4px"><div style="font-size:16px;font-weight:900;color:#16A34A">'+pmRelCnt+'</div><div style="font-size:7.5px;color:#16A34A;margin-top:1px">Released</div></div>'
        + '<div style="text-align:center;background:#FEF9C3;border-radius:5px;padding:7px 4px"><div style="font-size:16px;font-weight:900;color:#D97706">'+pmRdyCnt+'</div><div style="font-size:7.5px;color:#D97706;margin-top:1px">Ready</div></div>'
        + '<div style="text-align:center;background:#F1F5F9;border-radius:5px;padding:7px 4px"><div style="font-size:16px;font-weight:900;color:#6B7280">'+pmPndCnt+'</div><div style="font-size:7.5px;color:#6B7280;margin-top:1px">Pending</div></div>'
        + '</div>'
        // Contract release bar
        + '<div style="display:flex;justify-content:space-between;margin-bottom:4px">'
        + '<span style="font-size:8px;color:#555;font-weight:700">Contract Release</span>'
        + '<span style="font-size:8px;font-weight:900;color:#16A34A">'+pmPct+'% &nbsp;·&nbsp; '+(pmIsMulti ? pmCurLine : pmFirstCur+' '+pmRel.toLocaleString()+' of '+pmFirstCur+' '+pmTotal.toLocaleString())+'</span>'
        + '</div>'
        + '<div class="pb" style="height:8px;border-radius:5px"><div class="pf" style="width:'+pmPct+'%;background:linear-gradient(90deg,#16A34A,#22C55E);border-radius:5px"></div></div>'
        + '</div>';
    }

    // Footer
    html += '<div class="ftr">'
      + '<span>Executive Summary Report &nbsp;·&nbsp; '+esc(projectName)+' &nbsp;·&nbsp; Space Communication Technologies</span>'
      + '<span>'+date+'</span>'
      + '</div>';

    html += '</div></body></html>';
    pw.document.write(html);
    pw.document.close();
  }

  function exportOverview() {
    // Summary Report — phase and list level only, no tasks
    exportFullReport(false, false);
  }

  
  async function exportFullReport(showTasks=true, showComments=true) {
    const noComments = !showTasks; // noComments=true means Summary (no tasks shown)
    const pw = window.open('','_blank','width=1200,height=900');
    if (!pw) return;
    pw.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading...</title>
<style>body{font-family:Arial;display:flex;align-items:center;justify-content:center;height:100vh;background:#f4f6fb;color:#5B5BD6;font-size:18px;font-weight:700}
.sp{width:24px;height:24px;border:3px solid #5B5BD6;border-top-color:transparent;border-radius:50%;animation:spin 1s linear infinite;margin-right:10px;display:inline-block}
@keyframes spin{to{transform:rotate(360deg)}}</style></head>
<body><div class="sp"></div>Building Full Project Report…</body></html>`);
    pw.document.close();
    try {
      const projId = spaceId;
      const esc=s=>String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
      const fmt=d=>d?new Date(d.slice(0,10)+'T00:00:00').toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}):'—';
      const date=new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'});
      const SL={not_started:'Not Started',in_progress:'In Progress',completed:'Completed',on_hold:'On Hold',cancelled:'Cancelled',blocked:'Blocked',at_risk:'At Risk',review:'In Review'};
      const SC={not_started:'#8A93A6',in_progress:'#5B5BD6',completed:'#16A34A',on_hold:'#D97706',cancelled:'#9CA3AF',blocked:'#DC2626',at_risk:'#F76B15',review:'#0891B2'};
      const SBG={not_started:'#f1f5f9',in_progress:'#eff6ff',completed:'#f0fdf4',on_hold:'#fffbeb',cancelled:'#f9fafb',blocked:'#fef2f2',at_risk:'#fff7ed',review:'#f0f9ff'};
      const PC={urgent:'#DC2626',high:'#F76B15',normal:'#5B5BD6',low:'#16A34A'};
      const TI={task:'📋',milestone:'🏁',meeting:'📅'};

      const arcS=(pct,col,cx=80,cy=68,r=50,sw=11)=>{if(pct<=0)return '';const a=Math.PI*Math.min(pct/100,.9999);return '<path d="M '+(cx-r)+','+cy+' A '+r+','+r+' 0 0 1 '+(cx-r*Math.cos(a)).toFixed(1)+','+(cy-r*Math.sin(a)).toFixed(1)+'" fill="none" stroke="'+col+'" stroke-width="'+sw+'" stroke-linecap="round"/>';};
      const arcBgS=(cx=80,cy=68,r=50,sw=11)=>'<path d="M '+(cx-r)+','+cy+' A '+r+','+r+' 0 0 1 '+(cx+r)+','+cy+'" fill="none" stroke="#e8eaf0" stroke-width="'+sw+'" stroke-linecap="round"/>';

      const [dashRes,foldersRes]=await Promise.all([api.get('/spaces/'+projId+'/dashboard'),api.get('/spaces/'+projId+'/folders')]);
      const dash=dashRes||{};const stats=dash.stats||{};const folders=foldersRes||[];

      // FIX 5: Only include phases user has permission to see
      const accessibleFolders = [];
      for (const ph of folders) {
        try { await api.get('/folders/'+ph.id+'/lists'); accessibleFolders.push(ph); } catch(e) { /* no access */ }
      }
      const phasesData=await Promise.all(accessibleFolders.map(async ph=>{
        const lists=await api.get('/folders/'+ph.id+'/lists').catch(()=>[]);
        const listsWithTasks=await Promise.all((lists||[]).map(async ms=>{
          const tasks=await api.get('/lists/'+ms.id+'/tasks').catch(()=>[]);
          // Tasks from API already have nested .subtasks structure
          const allTasksFlat = tasks || [];
          if(noComments){
            // Skip all comment fetching — attach empty arrays
            function attachEmpty(tl){return(tl||[]).map(t=>({...t,comments:[],subtasks:attachEmpty(t.subtasks||[])}));}
            return{...ms,tasks:attachEmpty(allTasksFlat)};
          }
          // Collect ALL task IDs at any depth to fetch comments
          function collectAllIds(taskList) {
            const ids = [];
            (taskList||[]).forEach(t => { ids.push(t); if(t.subtasks?.length) ids.push(...collectAllIds(t.subtasks)); });
            return ids;
          }
          const allTasksDeep = collectAllIds(allTasksFlat);
          // Fetch comments for all tasks that have them
          const commentMap = {};
          await Promise.all(allTasksDeep.map(async t => {
            if ((t.comment_count||0) > 0) {
              const coms = await api.get('/tasks/'+t.id+'/comments').catch(()=>[]);
              commentMap[t.id] = coms || [];
            } else {
              commentMap[t.id] = [];
            }
          }));
          // Attach comments to the existing nested tree
          function attachComments(taskList) {
            return (taskList||[]).map(t => ({
              ...t,
              comments: commentMap[t.id] || [],
              subtasks: attachComments(t.subtasks)
            }));
          }
          const withComments = attachComments(allTasksFlat);
          return{...ms,tasks:withComments};
        }));
        return{...ph,lists:listsWithTasks};
      }));

      const hoursEst=parseFloat(dash.totalHours?.estimated||0);
      const hoursLog=parseFloat(dash.totalHours?.logged||0);
      const hoursPct=hoursEst>0?Math.min(100,Math.round((hoursLog/hoursEst)*100)):0;
      const hoursCol=hoursPct>90?'#DC2626':hoursPct>70?'#D97706':'#5B5BD6';
      const ovProg=Math.round((dash.overallProgress||0)*10)/10;
      const progCol=ovProg>=100?'#16A34A':ovProg>0?'#5B5BD6':'#aaa';
      const sd=dash.projectStartDate;
      const daysRun=sd?Math.max(0,Math.floor((Date.now()-new Date(String(sd).slice(0,10)+'T00:00:00'))/86400000)):null;

      const css=`*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,Helvetica,sans-serif;background:#fff;color:#1a1a2e;font-size:12px;line-height:1.5}.page{max-width:940px;margin:0 auto;padding:20px}h1{font-size:20px;font-weight:900;letter-spacing:-.3px}h2{font-size:14px;font-weight:800;margin-bottom:10px}.hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;padding-bottom:12px;border-bottom:2.5px solid #5B5BD6}.brand{font-size:9px;color:#5B5BD6;font-weight:800;letter-spacing:1px;text-transform:uppercase;text-align:right;line-height:1.8}.kpis{display:grid;grid-template-columns:repeat(7,1fr);gap:7px;margin-bottom:12px}.kpi{background:#f8f9fc;border-radius:7px;padding:8px 10px;border:1px solid #e8eaf0;text-align:center}.kpi .n{font-size:20px;font-weight:900;line-height:1}.kpi .l{font-size:8px;color:#888;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-top:2px}.kpi .s{font-size:9px;color:#aaa;margin-top:1px}.r3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:9px;margin-bottom:9px}.r2{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:9px}.card{background:#f8f9fc;border-radius:7px;border:1px solid #e8eaf0;padding:11px}.ct{font-size:8px;font-weight:800;text-transform:uppercase;letter-spacing:.9px;color:#888;margin-bottom:7px;padding-bottom:5px;border-bottom:1px solid #eee}table{width:100%;border-collapse:collapse}th{font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:#aaa;padding:4px 7px;border-bottom:1px solid #eee;text-align:left}td{border-bottom:1px solid #f4f4f8;font-size:11px;vertical-align:middle;padding:4px 7px}tr:last-child td{border-bottom:none}.sb4{display:grid;grid-template-columns:1fr 1fr;gap:5px;margin-top:7px}.sb{background:#fff;border-radius:5px;padding:5px 8px;border:1px solid #eee}.sl{font-size:8px;color:#888;font-weight:700;text-transform:uppercase;letter-spacing:.3px}.sv{font-size:13px;font-weight:900;margin-top:1px}.ph-hdr{background:#5B5BD6;color:#fff;padding:9px 12px;border-radius:7px 7px 0 0;display:flex;justify-content:space-between;align-items:center;page-break-after:avoid;page-break-inside:avoid}.ph-body{background:#f8f9fc;border:1px solid #5B5BD622;border-radius:0 0 7px 7px;padding:11px;margin-bottom:12px}.lhdr{background:#e8eaf0;padding:5px 9px;border-radius:5px;font-weight:700;font-size:11px;margin:7px 0 3px;color:#1a1a2e}.trow{padding:5px 7px;border-bottom:1px solid #f0f0f4;display:grid;grid-template-columns:22px 1fr 75px 90px 65px 65px 55px;gap:4px;align-items:center;font-size:11px}.trow:last-child{border-bottom:none}.pill{display:inline-block;font-size:9px;padding:2px 6px;border-radius:9px;font-weight:700}.cblock{background:#fff;border-left:3px solid #5B5BD6;padding:5px 9px;margin:3px 0;border-radius:0 5px 5px 0}.cmeta{font-size:9px;color:#888;margin-bottom:2px}.ctext{font-size:11px;color:#1a1a2e;white-space:pre-wrap}.pb{height:6px;background:#e8eaf0;border-radius:3px}.pf{height:100%;border-radius:3px}.ftr{margin-top:16px;padding-top:8px;border-top:1px solid #e8eaf0;display:flex;justify-content:space-between;font-size:9px;color:#aaa}.sb-break{page-break-before:always;padding-top:16px}.card{page-break-inside:avoid}.r2{page-break-inside:avoid}table{page-break-inside:auto}tr{page-break-inside:avoid}.ph-body{page-break-inside:auto}@media print{.no-print{display:none!important}body{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}@page{size:A4 portrait;margin:10mm}.page{padding:0;max-width:100%}.sb-break{page-break-before:always}}`;

      const gCard=(pct,col,lbl,sub)=>`<div style="text-align:center">
  <svg width="130" height="78" viewBox="0 0 160 90">
    <g>${arcBgS()}</g>
    <g>${arcS(pct,col)}</g>
    <text x="80" y="63" text-anchor="middle" font-size="24" font-weight="900" fill="${col}">${d1(pct)}%</text>
  </svg>
  ${lbl?`<div style="font-size:9px;color:#555;font-weight:600;letter-spacing:.3px;margin-top:2px">${esc(lbl)}</div>`:''}
  ${sub?`<div style="font-size:8px;color:#aaa;margin-top:1px;font-style:italic">${esc(sub)}</div>`:''}
</div>`;

      // ── Overview section
      let html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(space?.name||'Project')} — ${!showTasks?'Summary Report':!showComments?'Project Detail Report':'Full Project Report'}</title><style>${css}</style></head><body><div class="page">
<div class="hdr"><div><div style="font-size:9px;color:#5B5BD6;font-weight:800;text-transform:uppercase;letter-spacing:.8px;margin-bottom:3px">${!showTasks?'Summary Report':!showComments?'Project Detail Report':'Full Project Report'}</div><h1>🚀 ${esc(space?.name||'Project')}</h1><p style="font-size:11px;color:#888;margin-top:2px">Generated: ${date}</p></div><div class="brand">EOMP<br>Space Communication Technologies</div></div>
<button class="no-print" onclick="window.print()" style="position:fixed;top:10px;right:10px;padding:8px 20px;background:#5B5BD6;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;z-index:9999;box-shadow:0 4px 14px rgba(91,91,214,.4)">🖨 Print / Download PDF</button>
<div class="kpis">
  <div class="kpi"><div class="n" style="color:#5B5BD6">${stats.totalTasks||0}</div><div class="l">Total Tasks</div></div>
  <div class="kpi"><div class="n" style="color:#16A34A">${stats.completedTasks||0}</div><div class="l">Completed</div><div class="s">${stats.completionRate||0}%</div></div>
  <div class="kpi"><div class="n" style="color:#5B5BD6">${stats.inProgressTasks||0}</div><div class="l">In Progress</div></div>
  <div class="kpi"><div class="n" style="color:#F76B15">${stats.atRiskTasks||0}</div><div class="l">At Risk</div></div>
  <div class="kpi"><div class="n" style="color:#9333EA">${stats.escalatedTasks||0}</div><div class="l">Escalated</div></div>
  <div class="kpi"><div class="n" style="color:#DC2626">${stats.overdueTasks||0}</div><div class="l">Overdue</div></div>
  <div class="kpi"><div class="n" style="color:#0891B2">${daysRun!=null?daysRun:'—'}</div><div class="l">Days Running</div><div class="s">${sd?String(sd).slice(0,10):''}</div></div>
</div>
<div class="r3">
  <div class="card"><div class="ct">Overall Project Progress</div>${gCard(ovProg,progCol,'','Schedule Performance Index (SPI-weighted)')}<div class="sb4"><div class="sb"><div class="sl">Completed</div><div class="sv" style="color:#16A34A">${stats.completedTasks||0}</div></div><div class="sb"><div class="sl">Remaining</div><div class="sv" style="color:#5B5BD6">${(stats.totalTasks||0)-(stats.completedTasks||0)}</div></div></div></div>
  <div class="card"><div class="ct">Project Hours</div>${gCard(hoursPct,hoursCol,'','of budget used')}<div class="sb4">
    <div class="sb"><div class="sl">Budgeted</div><div class="sv" style="color:#888">${hoursEst.toFixed(1)}h</div></div>
    <div class="sb"><div class="sl">Logged</div><div class="sv" style="color:${hoursCol}">${hoursLog.toFixed(1)}h</div></div>
    <div class="sb"><div class="sl">Remaining</div><div class="sv" style="color:#16A34A">${Math.max(0,hoursEst-hoursLog).toFixed(1)}h</div></div>
    <div class="sb" style="background:${hoursLog>hoursEst?'#fef2f2':'#f0fdf4'}"><div class="sl" style="color:${hoursLog>hoursEst?'#DC2626':'#16A34A'}">${hoursLog>hoursEst?'Overrun':'On Track'}</div><div class="sv" style="color:${hoursLog>hoursEst?'#DC2626':'#16A34A'};font-size:12px">${hoursLog>hoursEst?'+'+(hoursLog-hoursEst).toFixed(1)+'h overrun':(hoursEst===0&&hoursLog===0)?'—':(hoursLog===0&&hoursEst>0)?'Not started':'✓ '+hoursPct+'% used'}</div></div>
  </div></div>
  <div class="card"><div class="ct">By Status</div>
    <table><thead><tr><th>Status</th><th style="text-align:center">N</th><th style="text-align:center">%</th></tr></thead><tbody>
    ${(dash.byStatus||[]).map(s=>`<tr><td><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${SC[s.status]||'#888'};margin-right:5px;vertical-align:middle"></span>${esc(SL[s.status]||(s.status.charAt(0).toUpperCase()+s.status.slice(1).replace(/_/g,' ')))}</td><td style="text-align:center;font-weight:700;color:${SC[s.status]||'#333'}">${s.count}</td><td style="text-align:center;color:#666">${stats.totalTasks>0?Math.round(s.count/stats.totalTasks*100):0}%</td></tr>`).join('')}
    </tbody></table>
    <div style="margin-top:7px;padding-top:6px;border-top:1px solid #eee"><div class="ct" style="margin-bottom:4px">Priority</div>
    <table><thead><tr><th>Priority</th><th style="text-align:center">Tasks</th></tr></thead><tbody>
    ${(dash.byPriority||[]).map(p=>`<tr><td><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${PC[p.priority]||'#888'};margin-right:5px;vertical-align:middle"></span>${esc((p.priority||'').charAt(0).toUpperCase()+(p.priority||'').slice(1))}</td><td style="text-align:center;font-weight:700">${p.count}</td></tr>`).join('')}
    </tbody></table></div>
  </div>
</div>`;
      // ── Phases Progress Summary
      html += '<div class="card" style="margin-bottom:10px;page-break-inside:avoid">'
            + '<div class="ct">Phases Progress Summary <span style="font-size:8px;color:#aaa;font-weight:400">(progress weighted by task weights)</span></div>'
            + '<table><thead><tr>'
            + '<th>Phase</th><th style="text-align:center">Phase Weight</th>'
            + '<th style="text-align:center">Lists</th><th style="text-align:center">Tasks</th>'
            + '<th style="text-align:center">Completed</th><th style="min-width:100px">Progress</th>'
            + '<th style="text-align:right">%</th>'
            + '</tr></thead><tbody>'
            + phasesData.map(ph => {
                const phT = (ph.lists||[]).flatMap(ms => (ms.tasks||[]).filter(t => !t.parent_task_id));
                const phDone = phT.filter(t => t.status === 'completed').length;
                const serverPh = (dash.phasesProgress||[]).find(p => p.name === ph.name);
                let phPct = 0;
                if (serverPh) {
                  phPct = Math.round(serverPh.progress || 0);
                } else {
                  let pw = 0, pp = 0;
                  phT.forEach(t => { const w = Math.max(t.task_weight||0, 1); pw += w; pp += w * (t.calculatedProgress ?? t.progress ?? 0) / 100; });
                  phPct = pw > 0 ? Math.round(pp / pw * 100) : 0;
                }
                const col = phPct >= 100 ? '#16A34A' : phPct > 50 ? '#5B5BD6' : phPct > 20 ? '#D97706' : '#DC2626';
                const lw = ph.weight || 0;
                return '<tr>'
                  + '<td style="font-weight:700">' + esc(ph.name) + '</td>'
                  + '<td style="text-align:center;color:#5B5BD6;font-weight:700">' + (lw > 0 ? lw + '%' : '—') + '</td>'
                  + '<td style="text-align:center">' + (ph.lists||[]).length + '</td>'
                  + '<td style="text-align:center">' + phT.length + '</td>'
                  + '<td style="text-align:center;color:#16A34A;font-weight:700">' + phDone + '</td>'
                  + '<td><div class="pb"><div class="pf" style="width:' + phPct + '%;background:' + col + ';min-width:' + (phPct > 0 ? 2 : 0) + 'px"></div></div></td>'
                  + '<td style="text-align:right;font-weight:700;color:' + col + '">' + phPct + '%</td>'
                  + '</tr>';
              }).join('')
            + '</tbody></table></div>';


      html+=`<div class="card" style="margin-bottom:10px"><div class="ct">List Progress Summary</div>
<table><thead><tr><th>List</th><th>Phase</th><th style="text-align:center">List Wt.</th><th style="text-align:center">Tasks</th><th style="text-align:center">Done</th><th style="min-width:80px">Progress</th><th style="text-align:right">%</th><th style="text-align:center">Budget (h)</th><th style="text-align:center">Logged (h)</th></tr></thead><tbody>
${(dash.listsProgress||[]).map(l=>{const pct=Math.round(l.progress||0);const col=pct>=100?'#16A34A':pct>0?'#5B5BD6':'#e8eaf0';const done=Math.round((l.taskCount||0)*pct/100);const lw=l.weight||l.list_weight||0;
return`<tr><td style="font-weight:600">${esc(l.name)}</td><td style="color:#5B5BD6;font-size:10px;font-weight:600">${esc(l.phase_name||'—')}</td><td style="text-align:center;color:#5B5BD6;font-weight:700">${lw>0?lw+'%':'—'}</td><td style="text-align:center">${l.taskCount||0}</td><td style="text-align:center;color:#16A34A;font-weight:700">${done}</td><td><div class="pb"><div class="pf" style="width:${pct}%;background:${col};min-width:${pct>0?2:0}px"></div></div></td><td style="text-align:right;font-weight:700;color:${col}">${pct}%</td><td style="text-align:center;color:#888">${l.budgetHours>0?l.budgetHours.toFixed(1):'—'}</td><td style="text-align:center;color:#5B5BD6">${l.loggedHours>0?l.loggedHours.toFixed(1):'—'}</td></tr>`}).join('')||'<tr><td colspan="9" style="color:#aaa;text-align:center;padding:7px">No data</td></tr>'}
</tbody></table></div>`;

      // Payment milestones
      html+=`<div class="sb-break"><div style="border-bottom:2px solid #5B5BD6;padding-bottom:7px;margin-bottom:12px"><h2>📊 Project Timeline (Phases & Lists)</h2></div>
<div style="display:flex;flex-wrap:wrap;gap:14px;align-items:center;margin-bottom:12px;font-size:8px;color:#555">
  <span style="display:inline-flex;align-items:center;gap:4px"><span style="width:14px;height:8px;background:#5B5BD6;border-radius:2px;display:inline-block"></span>Planned (scheduled window)</span>
  <span style="display:inline-flex;align-items:center;gap:4px"><span style="width:14px;height:8px;background:#16A34A;border-radius:2px;display:inline-block"></span>Completed</span>
  <span style="display:inline-flex;align-items:center;gap:4px"><span style="width:14px;height:6px;background:#fff7ed;border:1px solid #fb923c;border-radius:2px;display:inline-block"></span>Actual (elapsed)</span>
  <span style="display:inline-flex;align-items:center;gap:4px"><span style="font-weight:800;color:#16A34A">✓</span>On time</span>
  <span style="display:inline-flex;align-items:center;gap:4px"><span style="font-weight:800;color:#16A34A">▲ Nd</span>Finished N days early</span>
  <span style="display:inline-flex;align-items:center;gap:4px"><span style="font-weight:800;color:#DC2626">★ +Nd</span>Finished N days late</span>
  <span style="display:inline-flex;align-items:center;gap:4px"><span style="font-weight:800;color:#DC2626">⚠ Nd</span>Overdue by N days</span>
  <span style="display:inline-flex;align-items:center;gap:4px"><b>ES</b>=Earliest start &nbsp; <b>LF</b>=Latest finish</span>
</div>
<div style="margin:0 -4px">`;
      // Collect ALL dates — planned AND actual — so the scale covers the full picture
      const allDts=[];
      phasesData.forEach(ph=>(ph.lists||[]).forEach(ms=>(ms.tasks||[]).forEach(t=>{
        if(t.start_date&&t.start_date.length>=10)allDts.push(t.start_date.slice(0,10));
        if(t.due_date&&t.due_date.length>=10)allDts.push(t.due_date.slice(0,10));
        if(t.actual_start_date&&t.actual_start_date.length>=10)allDts.push(t.actual_start_date.slice(0,10));
        if(t.actual_end_date&&t.actual_end_date.length>=10)allDts.push(t.actual_end_date.slice(0,10));
        (t.subtasks||[]).forEach(st=>{
          if(st.start_date&&st.start_date.length>=10)allDts.push(st.start_date.slice(0,10));
          if(st.due_date&&st.due_date.length>=10)allDts.push(st.due_date.slice(0,10));
          if(st.actual_start_date&&st.actual_start_date.length>=10)allDts.push(st.actual_start_date.slice(0,10));
          if(st.actual_end_date&&st.actual_end_date.length>=10)allDts.push(st.actual_end_date.slice(0,10));
        });
      })));
      const minD=allDts.length>0?allDts.sort()[0]:null;
      const maxD=allDts.length>0?[...allDts].sort().slice(-1)[0]:null;
      // Use project date range or fallback to today ± 6 months
      const effMinD = minD || new Date(Date.now()-180*86400000).toISOString().slice(0,10);
      const effMaxD = maxD || new Date(Date.now()+180*86400000).toISOString().slice(0,10);
      if(true){
        const minD2=effMinD, maxD2=effMaxD;
        const totD=Math.max(1,Math.floor((new Date(maxD2+'T00:00:00')-new Date(minD2+'T00:00:00'))/86400000));
        const bp=(d1,d2)=>{if(!d1||!d2)return null;const s=Math.max(0,(new Date(d1+'T00:00:00')-new Date(minD2+'T00:00:00'))/86400000);const e=Math.min(totD,(new Date(d2+'T00:00:00')-new Date(minD2+'T00:00:00'))/86400000);return{l:Math.round(s/totD*100),w:Math.max(.5,Math.round((e-s)/totD*100))};};
        // Build time scale ticks — choose granularity by duration
        const buildTimeScale = (minDate, maxDate, totalDays, bp2) => {
          let ticks = '';
          const start = new Date(minDate+'T00:00:00');
          const end = new Date(maxDate+'T00:00:00');
          // Choose tick interval based on total duration
          let tickInterval, tickFmt, tickLabel;
          if (totalDays <= 90) {
            // Up to 3 months: weekly ticks
            tickInterval = 7;
            tickFmt = d => { const m=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return d.getDate()+' '+m[d.getMonth()]; };
          } else if (totalDays <= 548) {
            // Up to 18 months: monthly ticks
            tickInterval = null; // use month boundaries
            tickFmt = d => { const m=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return m[d.getMonth()]+' '+(d.getFullYear()%100<10?'0':'')+(d.getFullYear()%100); };
          } else {
            // Over 18 months: quarterly ticks
            tickInterval = null;
            tickFmt = d => { const q=Math.floor(d.getMonth()/3)+1; return 'Q'+q+' '+d.getFullYear(); };
          }

          let cursor = new Date(start);
          const seenLabels = new Set();
          while (cursor <= end) {
            const dateStr = cursor.toISOString().slice(0,10);
            const pos = bp2(dateStr, dateStr);
            if (pos) {
              const label = tickFmt(cursor);
              if (!seenLabels.has(label) && pos.l > 2 && pos.l < 97) {
                seenLabels.add(label);
                ticks += '<div style="position:absolute;left:'+pos.l+'%;top:0;height:100%;border-left:1px dashed #d1d5db"></div>'
                       + '<span style="position:absolute;left:'+pos.l+'%;top:0;font-size:7px;color:#9CA3AF;white-space:nowrap;transform:translateX(-50%);font-weight:600">'+label+'</span>';
              }
            }
            // Advance cursor
            if (tickInterval) {
              cursor.setDate(cursor.getDate() + tickInterval);
            } else if (totalDays <= 548) {
              // next month
              cursor = new Date(cursor.getFullYear(), cursor.getMonth()+1, 1);
            } else {
              // next quarter
              cursor = new Date(cursor.getFullYear(), cursor.getMonth()+3, 1);
            }
          }
          return ticks;
        };

        // Top scale: Summary Report only (Full Report has per-phase scales)
        if (noComments) {
          html += '<div style="display:flex;font-size:9px;color:#888;font-weight:700;margin-bottom:3px">'
            + '<div style="width:180px;flex-shrink:0">ITEM</div>'
            + '<div style="flex:1;position:relative;height:22px;border-bottom:1px solid #e5e7eb">'
            + '<span style="position:absolute;left:0;bottom:2px;font-size:7.5px;color:#555;font-weight:700">'+fmt(minD2)+'</span>'
            + '<span style="position:absolute;right:0;bottom:2px;font-size:7.5px;color:#555;font-weight:700">'+fmt(maxD2)+'</span>'
            + buildTimeScale(minD2, maxD2, totD, bp)
            + '</div>'
            + '<div style="flex-shrink:0;width:26px"></div>'
            + '<div style="flex-shrink:0;width:58px"></div>'
            + '</div>';
        }
          +'<div style="display:flex;gap:12px;margin-bottom:8px;font-size:9px;align-items:center;padding-left:184px">'
          +'<span style="display:flex;align-items:center;gap:4px"><span style="display:inline-block;width:18px;height:8px;background:#5B5BD6;border-radius:2px"></span><span style="font-size:8px;color:#555">Planned</span></span>'
          +'<span style="display:flex;align-items:center;gap:4px"><span style="display:inline-block;width:18px;height:5px;background:#F97316;border-radius:2px"></span><span style="font-size:8px;color:#555">Actual</span></span>'
          +'<span style="font-size:8px;color:#aaa;font-style:italic">Status indicators shown at right of each row</span>'
          +'</div>';
        phasesData.forEach(ph=>{
          // Collect all dates for this phase (tasks + subtasks)
          const phD=[];
          (ph.lists||[]).forEach(ms=>(ms.tasks||[]).forEach(t=>{
            if(t.start_date&&t.start_date.length>=10)phD.push(t.start_date.slice(0,10));
            if(t.due_date&&t.due_date.length>=10)phD.push(t.due_date.slice(0,10));
            (t.subtasks||[]).forEach(st=>{
              if(st.start_date&&st.start_date.length>=10)phD.push(st.start_date.slice(0,10));
              if(st.due_date&&st.due_date.length>=10)phD.push(st.due_date.slice(0,10));
            });
          }));
          // ES = Early Start, LF = Late Finish (PMI)
          // Use phase dates if available, fallback to project dates
          const phSorted=phD.sort();
          const phESDate=phSorted.length>0?phSorted[0]:minD;
          const phLFDate=phSorted.length>0?phSorted[phSorted.length-1]:maxD;
          const phB=bp(phESDate, phLFDate);
          // Per-phase scale — Full Report: full scale before every phase
          // Summary Report: no per-phase scale (single top scale is enough)
          if (!noComments) {
            html += '<div style="display:flex;font-size:9px;color:#888;font-weight:600;margin-bottom:2px;margin-top:8px">'
              + '<div style="width:180px;flex-shrink:0;font-size:8px;color:#9CA3AF">TIME SCALE</div>'
              + '<div style="flex:1;position:relative;height:20px;border-bottom:2px solid #e5e7eb;margin-left:4px">'
              + '<span style="position:absolute;left:0;bottom:2px;font-size:7px;color:#555;font-weight:700">'+fmt(minD2)+'</span>'
              + '<span style="position:absolute;right:0;bottom:2px;font-size:7px;color:#555;font-weight:700">'+fmt(maxD2)+'</span>'
              + buildTimeScale(minD2, maxD2, totD, bp)
              + '</div>'
              + '<div style="flex-shrink:0;width:26px"></div>'
              + '<div style="flex-shrink:0;width:58px"></div>'
              + '</div>';
          }
          html += '<div style="display:flex;align-items:center;margin-bottom:3px;margin-top:13px">'
            + '<div style="width:180px;flex-shrink:0;font-size:11px;font-weight:700;color:#fff;background:#5B5BD6;padding:3px 8px;border-radius:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + esc(ph.name) + '</div>'
            + '<div style="flex:1;background:#f0f0f8;height:18px;border-radius:3px;position:relative;margin-left:4px">'
            + (phB ? '<div style="position:absolute;left:'+phB.l+'%;width:'+phB.w+'%;height:100%;background:#5B5BD6;border-radius:3px;opacity:.75"></div>'
                   + (phB.w>=22 ? '<span style="position:absolute;left:'+phB.l+'%;top:-9px;font-size:7px;color:#5B5BD6;font-weight:700;white-space:nowrap">ES '+fmt(phESDate)+'</span>' : '')
                   + '<span style="position:absolute;right:'+(100-(phB.l+phB.w))+'%;top:-9px;font-size:7px;color:#5B5BD6;font-weight:700;white-space:nowrap">LF '+fmt(phLFDate)+'</span>'
                   : '<div style="position:absolute;left:0;width:100%;height:100%;background:#5B5BD688;border-radius:3px"></div>'
                     + '<span style="position:absolute;left:0;top:-9px;font-size:7px;color:#5B5BD6;font-weight:700">ES '+fmt(phESDate)+'</span>'
                     + '<span style="position:absolute;right:0;top:-9px;font-size:7px;color:#5B5BD6;font-weight:700">LF '+fmt(phLFDate)+'</span>')
            + '</div>'
            + '<div style="flex-shrink:0;width:26px"></div>'
            + '<div style="flex-shrink:0;width:58px"></div>'
            + '</div>';
          (ph.lists||[]).forEach(ms=>{
            // Planned dates (start_date / due_date)
            const msD=[];(ms.tasks||[]).forEach(t=>{if(t.start_date)msD.push(t.start_date.slice(0,10));if(t.due_date)msD.push(t.due_date.slice(0,10));});
            const msB=msD.length>=2?bp(msD.sort()[0],[...msD].sort().slice(-1)[0]):null;
            // Actual dates — ONLY use real actual_start_date / actual_end_date
            // No fallback to planned dates: if actuals are missing, the bar
            // is positioned by progress % only (full-width fill), not by dates
            // This ensures planned bar and actual bar are always independent
            const msAD=[];
            (ms.tasks||[]).forEach(t=>{
              if(t.actual_start_date&&t.actual_start_date.length>=10) msAD.push(t.actual_start_date.slice(0,10));
              if(t.actual_end_date&&t.actual_end_date.length>=10) msAD.push(t.actual_end_date.slice(0,10));
            });
            // msAB: position the actual bar in the scale ONLY when real actual dates exist
            const msAB=msAD.length>=2?bp(msAD.sort()[0],[...msAD].sort().slice(-1)[0]):null;
            const mt=(ms.tasks||[]).filter(t=>!t.parent_task_id);
            // Use server-calculated progress from listsProgress (matches what list view shows)
            // Use server-calculated progress — same source as Phase Dashboard web view
            const serverList=(dash.listsProgress||[]).find(l=>l.name===ms.name);
            const mpct=serverList?Math.round(serverList.progress||0):Math.round(ms.progress||0);
            // ── Professional planned vs actual bar ─────────────────────────────
            // Planned fill color based on completion state
            const barCol2 = mpct >= 100 ? '#16A34A' : mpct > 0 ? '#5B5BD6' : '#9CA3AF';

            // ── Delay indicator ──────────────────────────────────────────────
            // Only care about LATE completion — early start is irrelevant
            const today = new Date().toISOString().slice(0,10);
            // Planned end = latest due_date across list tasks
            const msDueDates = (ms.tasks||[]).filter(t=>t.due_date).map(t=>t.due_date.slice(0,10));
            const msPlannedEnd = msDueDates.length > 0 ? [...msDueDates].sort().slice(-1)[0] : null;
            // Actual end = latest actual_end_date OR today if still in progress
            const msActualEndDates = (ms.tasks||[]).filter(t=>t.actual_end_date).map(t=>t.actual_end_date.slice(0,10));
            const msActualEnd = msActualEndDates.length > 0 ? [...msActualEndDates].sort().slice(-1)[0] : null;
            const isCompleted = mpct >= 100;
            const isInProgress = mpct > 0 && mpct < 100;
            const isNotStarted = mpct === 0;

            // ── Schedule Variance — 3 states only, relative to report date ──
            let delayIndicator = '';
            if (msPlannedEnd) {
              if (isCompleted) {
                const finishDate = msActualEnd || today;
                const daysDiff = Math.round((new Date(finishDate+'T00:00:00') - new Date(msPlannedEnd+'T00:00:00')) / 86400000);
                if (daysDiff > 1) {
                  // ★ LATE — completed after planned finish
                  delayIndicator = '<span style="font-size:8px;font-weight:800;color:#DC2626;white-space:nowrap;margin-left:6px;padding:2px 6px;background:#fee2e2;border-radius:4px;border:1px solid #fca5a5">★ +'+daysDiff+'d</span>';
                } else if (daysDiff < -1) {
                  // ▲ EARLY — completed before planned finish
                  const daysEarly = Math.abs(daysDiff);
                  delayIndicator = '<span style="font-size:8px;font-weight:700;color:#16A34A;white-space:nowrap;margin-left:6px;padding:2px 6px;background:#dcfce7;border-radius:4px;border:1px solid #86efac">▲ '+daysEarly+'d</span>';
                } else {
                  // ✓ ON TIME — within 1-day tolerance
                  delayIndicator = '<span style="font-size:8px;font-weight:700;color:#16A34A;white-space:nowrap;margin-left:6px;padding:2px 6px;background:#dcfce7;border-radius:4px;border:1px solid #86efac">✓</span>';
                }
              } else if (isInProgress && today > msPlannedEnd) {
                // ⚠ OVERDUE — still running, past planned finish (as of report date)
                const daysOverdue = Math.round((new Date(today+'T00:00:00') - new Date(msPlannedEnd+'T00:00:00')) / 86400000);
                delayIndicator = '<span style="font-size:8px;font-weight:800;color:#DC2626;white-space:nowrap;margin-left:6px;padding:2px 6px;background:#fee2e2;border-radius:4px;border:1px solid #fca5a5">⚠ '+daysOverdue+'d</span>';
              }
              // No indicator: not started, in progress within schedule
            }

            html += '<div style="display:flex;align-items:center;margin-bottom:5px">'
              // List name
              + '<div style="width:180px;flex-shrink:0;font-size:10px;color:#5B5BD6;padding:2px 6px 2px 14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">≡ '+esc(ms.name)+'</div>'
              + '<div style="flex:1;position:relative;margin-left:4px">'

              // ── PLANNED BAR (top, 10px) ────────────────────────────────────
              // Full light background = scheduled window | colored fill = % done
              + '<div style="position:relative;height:10px;background:#e8eaf0;border-radius:3px;margin-bottom:2px">'
              + (msB
                // With dates: show scheduled window + progress fill
                ? '<div style="position:absolute;left:'+msB.l+'%;width:'+msB.w+'%;height:100%;background:#c7d2fe;border-radius:3px"></div>'
                  + '<div style="position:absolute;left:'+msB.l+'%;width:'+Math.round(msB.w*Math.min(mpct,100)/100)+'%;height:100%;background:'+barCol2+';border-radius:3px"></div>'
                // No dates: just show % as full-width fill
                : '<div style="position:absolute;left:0;width:'+mpct+'%;height:100%;background:'+barCol2+';border-radius:3px"></div>'
              )
              + '</div>'
              // % label OUTSIDE the bar — always readable
              // (positioned as a separate element after the bars, before delay indicator)

              // ── ACTUAL BAR (bottom, 6px) — only when work has started ──────
              // Orange = actual elapsed time. Positioned by actual start/end dates.
              + ((!isNotStarted)
                ? '<div style="position:relative;height:6px;background:#fff7ed;border-radius:3px;border:1px solid #fed7aa">'
                  + (msAB
                    // With actual dates: show actual window
                    ? '<div style="position:absolute;left:'+msAB.l+'%;width:'+msAB.w+'%;height:100%;background:#FDBA74;border-radius:3px"></div>'
                      + '<div style="position:absolute;left:'+msAB.l+'%;width:'+Math.round(msAB.w*Math.min(mpct,100)/100)+'%;height:100%;background:#F97316;border-radius:3px"></div>'
                    // No actual dates: progress as full-width orange
                    : '<div style="position:absolute;left:0;width:'+mpct+'%;height:100%;background:#F97316;border-radius:3px"></div>'
                  )
                  + '</div>'
                : ''
              )

              + '</div>'
              // % label — fixed 34px outside bar zone, always readable
              + '<div style="flex-shrink:0;width:26px;text-align:right;font-size:8px;font-weight:800;color:#374151;padding-right:2px">'+mpct+'%</div>'
              // Indicator — fixed 68px outside bar zone, never compresses bars
              + '<div style="flex-shrink:0;width:58px;">'+delayIndicator+'</div>'
              + '</div>';

            // ── Task bars — Full Report only (not Summary) ──────────────────
            if (!noComments) {
              const topTasks = (ms.tasks||[])
                .filter(t => !t.parent_task_id)
                .sort((a,b) => (a.position||0)-(b.position||0));

              topTasks.forEach(t => {
                // Task planned dates
                const tPD = [];
                if(t.start_date&&t.start_date.length>=10) tPD.push(t.start_date.slice(0,10));
                if(t.due_date&&t.due_date.length>=10) tPD.push(t.due_date.slice(0,10));
                const tB = tPD.length>=2 ? bp(tPD.sort()[0],tPD.slice(-1)[0]) : null;

                // Task actual dates — only real actuals, no fallback
                const tAD = [];
                if(t.actual_start_date&&t.actual_start_date.length>=10) tAD.push(t.actual_start_date.slice(0,10));
                if(t.actual_end_date&&t.actual_end_date.length>=10) tAD.push(t.actual_end_date.slice(0,10));
                const tAB = tAD.length>=2 ? bp(tAD.sort()[0],tAD.slice(-1)[0]) : null;

                // Task progress
                const tPct = Math.round(t.calculatedProgress ?? t.progress ?? 0);
                const tCol = tPct>=100?'#16A34A':tPct>0?'#5B5BD6':'#9CA3AF';
                const tIsCompleted = tPct >= 100;
                const tIsInProgress = tPct > 0 && tPct < 100;

                // Task planned end for indicator
                const tPlannedEnd = t.due_date ? t.due_date.slice(0,10) : null;
                const tActualEnd = t.actual_end_date ? t.actual_end_date.slice(0,10) : null;
                const tToday = today;

                // Task delay indicator
                let tIndicator = '';
                if (tPlannedEnd) {
                  if (tIsCompleted) {
                    const tFinish = tActualEnd || tToday;
                    const tDiff = Math.round((new Date(tFinish+'T00:00:00')-new Date(tPlannedEnd+'T00:00:00'))/86400000);
                    if (tDiff > 1)
                      tIndicator = '<span style="font-size:7px;font-weight:800;color:#DC2626;padding:1px 4px;background:#fee2e2;border-radius:3px;border:1px solid #fca5a5">★ +'+tDiff+'d</span>';
                    else if (tDiff < -1)
                      tIndicator = '<span style="font-size:7px;font-weight:700;color:#16A34A;padding:1px 4px;background:#dcfce7;border-radius:3px;border:1px solid #86efac">▲ '+Math.abs(tDiff)+'d</span>';
                    else
                      tIndicator = '<span style="font-size:7px;font-weight:700;color:#16A34A;padding:1px 4px;background:#dcfce7;border-radius:3px;border:1px solid #86efac">✓</span>';
                  } else if (tIsInProgress && tToday > tPlannedEnd) {
                    const tOver = Math.round((new Date(tToday+'T00:00:00')-new Date(tPlannedEnd+'T00:00:00'))/86400000);
                    tIndicator = '<span style="font-size:7px;font-weight:800;color:#DC2626;padding:1px 4px;background:#fee2e2;border-radius:3px;border:1px solid #fca5a5">⚠ '+tOver+'d</span>';
                  }
                }

                html += '<div style="display:flex;align-items:center;margin-bottom:3px">'
                  // Task name — indented more than list (28px), smaller font
                  + '<div style="width:180px;flex-shrink:0;font-size:9px;color:#374151;padding:1px 6px 1px 28px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;opacity:.9">· '+esc(t.name)+'</div>'
                  // Bar zone
                  + '<div style="flex:1;position:relative;margin-left:4px">'
                  // Planned bar (7px)
                  + '<div style="position:relative;height:7px;background:#e8eaf0;border-radius:2px;margin-bottom:2px">'
                  + (tB
                    ? '<div style="position:absolute;left:'+tB.l+'%;width:'+tB.w+'%;height:100%;background:#c7d2fe;border-radius:2px"></div>'
                      + '<div style="position:absolute;left:'+tB.l+'%;width:'+Math.round(tB.w*Math.min(tPct,100)/100)+'%;height:100%;background:'+tCol+';border-radius:2px"></div>'
                    : '<div style="position:absolute;left:0;width:'+tPct+'%;height:100%;background:'+tCol+';border-radius:2px"></div>'
                  )
                  + '</div>'
                  // Actual bar (4px) — only when started
                  + (tPct > 0
                    ? '<div style="position:relative;height:4px;background:#fff7ed;border-radius:2px;border:1px solid #fed7aa">'
                      + (tAB
                        ? '<div style="position:absolute;left:'+tAB.l+'%;width:'+tAB.w+'%;height:100%;background:#FDBA74;border-radius:2px"></div>'
                          + '<div style="position:absolute;left:'+tAB.l+'%;width:'+Math.round(tAB.w*Math.min(tPct,100)/100)+'%;height:100%;background:#F97316;border-radius:2px"></div>'
                        : '<div style="position:absolute;left:0;width:'+tPct+'%;height:100%;background:#F97316;border-radius:2px"></div>'
                      )
                      + '</div>'
                    : ''
                  )
                  + '</div>'
                  // % label
                  + '<div style="flex-shrink:0;width:26px;text-align:right;font-size:7px;font-weight:700;color:#6b7280;padding-right:2px">'+tPct+'%</div>'
                  // Indicator
                  + '<div style="flex-shrink:0;width:58px;padding-left:3px">'+tIndicator+'</div>'
                  + '</div>';
              });
            }
          });
        });
      } else {html+='<p style="color:#aaa;font-size:12px;padding:10px">No dates set on tasks — add start/due dates to see the timeline.</p>';}
      html+='</div>';

      // ── Phase details ────────────────────────────────────────────────────────
      for(const ph of phasesData){
        const phT=(ph.lists||[]).flatMap(ms=>(ms.tasks||[]).filter(t=>!t.parent_task_id));
        const phDone=phT.filter(t=>t.status==='completed').length;
        const phIP=phT.filter(t=>t.status==='in_progress').length;
        const phBgt=phT.reduce((s,t)=>s+(t.time_budget||0),0);
        const phLgt=phT.reduce((s,t)=>s+(t.time_estimate||0),0);
        // Use server-calculated phase progress if available; otherwise weight-based
        // Use server-calculated phase progress if available
        const serverPhase=(dash.phasesProgress||[]).find(p=>p.name===ph.name);
        let phW=0,phP=0;
        phT.forEach(t=>{const w=Math.max(t.task_weight||0,1);phW+=w;phP+=w*(t.calculatedProgress??t.progress??0)/100;});
        const phPct=serverPhase?Math.round(serverPhase.progress||0):(phW>0?Math.round(phP/phW*100):0);
        const phC=phPct>=100?'#16A34A':phPct>50?'#5B5BD6':phPct>20?'#D97706':'#DC2626';

        html+=`<div class="sb-break"><div style="page-break-inside:avoid">
<div class="ph-hdr">
  <span>📁 ${esc(ph.name)}</span>
  <span style="display:flex;align-items:center;gap:10px;font-size:11px;opacity:.9">
    <span style="background:rgba(255,255,255,.18);padding:2px 8px;border-radius:10px;font-size:10px">Phase Weight: ${ph.weight||0}%</span>
    ${phT.length} tasks &nbsp;·&nbsp; ${phPct}% complete &nbsp;·&nbsp; ${phDone} completed
  </span>
</div>
<div class="ph-body">
  <div class="r3" style="margin-bottom:9px">
    <div class="card"><div class="ct">Phase Summary</div>
      <table><tbody>
        <tr><td style="color:#888">Total Tasks</td><td style="font-weight:700">${phT.length}</td></tr>
        <tr><td style="color:#888">Completed</td><td style="font-weight:700;color:#16A34A">${phDone}</td></tr>
        <tr><td style="color:#888">In Progress</td><td style="font-weight:700;color:#5B5BD6">${phIP}</td></tr>
        <tr><td style="color:#888">Remaining</td><td style="font-weight:700">${phT.length-phDone}</td></tr>
        <tr><td style="color:#888">Progress</td><td style="font-weight:700;color:${phC}">${phPct}%</td></tr>
        <tr><td style="color:#888">Budget</td><td style="font-weight:700">${phBgt>0?phBgt.toFixed(1)+'h':'—'}</td></tr>
        <tr><td style="color:#888">Logged</td><td style="font-weight:700;color:#5B5BD6">${phLgt>0?phLgt.toFixed(1)+'h':'—'}</td></tr>
      </tbody></table>
    </div>
    <div class="card"><div class="ct">Lists in Phase
        <span style="font-size:8px;color:#aaa;font-weight:400;margin-left:6px">(weighted by task weights within each list)</span></div>
      <table><thead><tr><th>List</th><th style="text-align:center">Tasks</th><th style="text-align:center">List Weight</th><th style="text-align:right">Progress %</th></tr></thead><tbody>
      ${(ph.lists||[]).map(ms=>{
        const mt=(ms.tasks||[]).filter(t=>!t.parent_task_id);
        // Use server-calculated list progress (matches Phase Dashboard web view)
        const mpct=Math.round(ms.progress||0);
        const col=mpct>=100?'#16A34A':mpct>0?'#5B5BD6':'#888';
        const lw=ms.weight||0;
        return`<tr><td style="font-size:11px">${esc(ms.name)}</td><td style="text-align:center">${mt.length}</td><td style="text-align:center;color:${lw>0?'#5B5BD6':'#aaa'};font-weight:${lw>0?'700':'400'}">${lw>0?lw+'%':'—'}</td><td style="text-align:right;font-weight:700;color:${col}">${mpct}%</td></tr>`;
      }).join('')}
      </tbody></table>
    </div>
    <div class="card"><div class="ct">Status Breakdown</div>
      <table><thead><tr><th>Status</th><th style="text-align:center">Count</th></tr></thead><tbody>
      ${Object.entries(phT.reduce((a,t)=>{a[t.status]=(a[t.status]||0)+1;return a;},{})).map(([s,n])=>`<tr><td><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${SC[s]||'#888'};margin-right:5px;vertical-align:middle"></span>${esc(SL[s]||(s.charAt(0).toUpperCase()+s.slice(1).replace(/_/g,' ')))}</td><td style="text-align:center;font-weight:700;color:${SC[s]||'#333'}">${n}</td></tr>`).join('')||'<tr><td colspan="2" style="color:#aaa;text-align:center;padding:6px">No data</td></tr>'}
      </tbody></table>
    </div>
  </div>
  ${(ph.lists||[]).map(ms=>{
    const topT=(ms.tasks||[]).filter(t=>!t.parent_task_id);
    if(!topT.length)return'';
    return`<div class="lhdr">≡ ${esc(ms.name)} (${topT.length} tasks)</div>
    <div style="background:#fff;border-radius:6px;margin-bottom:7px;overflow:hidden;border:1px solid #eee">
      <div class="trow" style="background:#f8f9fc;font-weight:700;font-size:9px;color:#888"><div></div><div>TASK NAME</div><div>TYPE</div><div>STATUS</div><div>START DATE</div><div>DUE DATE</div><div style="text-align:right">PROG</div></div>
      ${topT.map(t=>{
        const stSt=`background:${SBG[t.status]||'#f9fafb'};color:${SC[t.status]||'#666'}`;
        const subs=(ms.tasks||[]).filter(s=>s.parent_task_id===t.id);
        const hasC=(t.comments||[]).length>0;
        let th=`<div class="trow" style="background:${t.status==='completed'?'#f0fdf4':'#fff'}">
          <div style="font-size:13px">${TI[t.task_type]||'📋'}</div>
          <div style="font-weight:600;font-size:11px">${esc(t.name)}${t.assignees?.length?`<div style="font-size:9px;color:#888;margin-top:1px">👤 ${t.assignees.map(a=>esc(a.full_name)).join(', ')}</div>`:''}</div>
          <div style="font-size:10px;color:#888">${esc((t.task_type||'').charAt(0).toUpperCase()+(t.task_type||'').slice(1))}</div>
          <div><span class="pill" style="${stSt}">${esc(SL[t.status]||(t.status.charAt(0).toUpperCase()+t.status.slice(1).replace(/_/g,' ')))}</span></div>
          <div style="font-size:10px;color:#777">${fmt(t.start_date)}</div>
          <div style="font-size:11px;color:${t.due_date&&t.due_date<new Date().toISOString().slice(0,10)&&t.status!=='completed'?'#DC2626':'#666'}">${fmt(t.due_date)}</div>
          <div style="text-align:right;font-weight:700;color:${(t.calculatedProgress??t.progress??0)>=100?'#16A34A':(t.calculatedProgress??t.progress??0)>0?'#5B5BD6':'#888'}">${Math.round(t.calculatedProgress??t.progress??0)}%</div>
        </div>`;
        if(subs.length)th+=subs.map(st=>`<div class="trow" style="background:#fafafa"><div style="font-size:10px;color:#aaa">↳</div><div style="font-size:10px;color:#555">${esc(st.name)}${st.assignees?.length?`<div style="font-size:8px;color:#aaa">👤 ${st.assignees.map(a=>esc(a.full_name)).join(', ')}</div>`:''}</div><div style="font-size:9px;color:#aaa">${TI[st.task_type]||'📋'}</div><div><span class="pill" style="background:${SBG[st.status]||'#f9fafb'};color:${SC[st.status]||'#666'};font-size:8px">${esc(SL[st.status]||(st.status.charAt(0).toUpperCase()+st.status.slice(1).replace(/_/g,' ')))}</span></div><div style="font-size:10px;color:#666">${fmt(st.due_date)}</div><div style="text-align:right;font-size:10px;color:#888">${Math.round(st.calculatedProgress??st.progress??0)}%</div></div>`).join('');
        // Comments — only render if not in noComments mode
        const collectAllComments=(task,depth=0)=>{
          const items=[];
          if((task.comments||[]).length>0){
            items.push({name:depth===0?null:task.name,comments:task.comments,depth});
          }
          (task.subtasks||[]).forEach(st=>items.push(...collectAllComments(st,depth+1)));
          return items;
        };
        const allCommentGroups=noComments?[]:collectAllComments(t);
        const totalComments=allCommentGroups.reduce((s,g)=>s+g.comments.length,0);
        if(!noComments&&totalComments>0)th+=`<div style="padding:5px 7px 7px 36px;background:#fff">
          <div style="font-size:9px;font-weight:700;color:#5B5BD6;margin-bottom:5px;text-transform:uppercase;letter-spacing:.5px">💬 Comments & Escalations (${totalComments})</div>
          ${allCommentGroups.map(g=>`
            ${g.name?`<div style="font-size:9px;font-weight:700;color:#8B5CF6;margin:6px 0 3px;padding:2px 6px;background:#F5F3FF;border-radius:4px;display:inline-block">↳ Subtask: ${esc(g.name)}</div>`:''}
            ${g.comments.map(cm=>{
              const dt=cm.created_at?new Date(cm.created_at).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}):'';
              const isEsc=cm.content&&cm.content.startsWith('🚨 ESCALATION');
              return`<div class="cblock" style="border-left-color:${isEsc?'#DC2626':'#5B5BD6'};background:${isEsc?'#fff5f5':'#fff'}">
                <div class="cmeta"><strong style="color:#1a1a2e">${esc(cm.full_name||'?')}</strong> &nbsp;·&nbsp; ${dt}${isEsc?' &nbsp;<span style="color:#DC2626;font-weight:700;font-size:9px">🚨 ESCALATION</span>':''}</div>
                <div class="ctext">${esc(cm.content)}</div>
              </div>`;
            }).join('')}
          `).join('')}
        </div>`;
        return th;
      }).join('')}
    </div>`;
  }).join('')}
</div></div></div>`;
      }

      html+=`</div>
`;
      html+=`
<div class="r2">
  <div class="card"><div class="ct">Team Workload</div><table><thead><tr><th>Member</th><th style="text-align:center">Tasks</th><th style="text-align:center">Hours</th></tr></thead><tbody>
  ${(dash.workload||[]).map(w=>`<tr><td>${esc(w.full_name)}</td><td style="text-align:center;font-weight:700;color:#5B5BD6">${w.active_count||w.count||0}</td><td style="text-align:center;color:#666">${(w.total_hours||0).toFixed(1)}h</td></tr>`).join('')||'<tr><td colspan="3" style="color:#aaa;text-align:center;padding:7px">No data</td></tr>'}
  </tbody></table></div>
  <div class="card"><div class="ct">Open Tasks by Assignee</div><table><thead><tr><th>Member</th><th style="text-align:center">Open</th><th style="text-align:center">In Prog</th><th style="text-align:center">At Risk</th></tr></thead><tbody>
  ${(dash.openByAssignee||[]).map(w=>`<tr><td>${esc(w.full_name)}</td><td style="text-align:center;font-weight:700">${w.open_count||0}</td><td style="text-align:center;color:#5B5BD6">${w.in_progress_count||0}</td><td style="text-align:center;color:#F76B15">${w.at_risk_count||0}</td></tr>`).join('')||'<tr><td colspan="4" style="color:#aaa;text-align:center;padding:7px">No data</td></tr>'}
  </tbody></table></div>
</div>`;
        const pms=dash.paymentMilestones||[];
      if(pms.length>0){
        const pmRdy=pms.filter(p=>p.status==='ready_for_release').length;
        const pmC={pending:'#8A93A6',ready_for_release:'#D97706',released:'#16A34A'};
        const sorted=[...pms].sort((a,b)=>{const o={released:0,ready_for_release:1,pending:2};return(o[a.status]??2)-(o[b.status]??2)||((a.planned_date||'z').localeCompare(b.planned_date||'z'));});
        const pmBC2={};
        pms.forEach(p=>{const c=p.currency||'OMR';if(!pmBC2[c]){pmBC2[c]={total:0,released:0};}pmBC2[c].total+=parseFloat(p.amount||0);const _b=(p.releaseLog||[]).reduce((s,r)=>s+parseFloat(r.amount||0),0);pmBC2[c].released+=(_b>0?_b:(p.status==='released'?parseFloat(p.amount||0):0));});
        const pmCurs2=Object.keys(pmBC2);
        const tcCards2=pmCurs2.map(c=>'<div style="background:#EEF2FF;border-radius:6px;padding:8px 10px;border:1px solid #C7D2FE;text-align:center"><div style="font-size:8px;color:#5B5BD6;font-weight:800;text-transform:uppercase">Contract ('+c+')</div><div style="font-size:14px;font-weight:900;color:#5B5BD6;margin-top:3px">'+c+' '+pmBC2[c].total.toLocaleString()+'</div></div>').join('');
        const relCards2=pmCurs2.map(c=>'<div style="background:#F0FDF4;border-radius:6px;padding:8px 10px;border:1px solid #BBF7D0;text-align:center"><div style="font-size:8px;color:#16A34A;font-weight:800;text-transform:uppercase">Released ('+c+')</div><div style="font-size:14px;font-weight:900;color:#16A34A;margin-top:3px">'+c+' '+pmBC2[c].released.toLocaleString()+'</div></div>').join('');
        const rdyCard2='<div style="background:#FFFBEB;border-radius:6px;padding:8px 10px;border:1px solid #FDE68A;text-align:center"><div style="font-size:8px;color:#D97706;font-weight:800;text-transform:uppercase">Ready</div><div style="font-size:14px;font-weight:900;color:#D97706;margin-top:3px">'+pmRdy+' item'+(pmRdy!==1?'s':'')+'</div></div>';
        const gc2='repeat('+(pmCurs2.length*2+1)+',1fr)';
        html += '<div class="card" style="margin-bottom:10px;page-break-before:always"><div class="ct">Payment Milestones</div>'
          + '<div style="display:grid;grid-template-columns:'+gc2+';gap:7px;margin-bottom:8px">'
          + tcCards2 + relCards2 + rdyCard2
          + '</div>'
          + '<table><thead><tr><th>Milestone</th><th>Planned</th><th>Actual Released</th><th>Variance</th><th>Status</th><th>Planned Date</th><th>Released Date</th></tr></thead><tbody>'
          + sorted.map(pm=>'<tr>'
              +'<td style="font-size:11px">'+esc(pm.name)+'</td>'
              +(function(){var ar=(pm.releaseLog||[]).reduce(function(s,r){return s+parseFloat(r.amount||0)},0),pl=parseFloat(pm.amount||0),v=ar>0?ar-pl:0,vc=v>0?'#B45309':v<0?'#2563EB':'#16A34A';return '<td style="font-weight:700;color:#5B5BD6;white-space:nowrap">'+esc(pm.currency||'OMR')+' '+pl.toLocaleString()+'</td>'+'<td style="font-weight:700;white-space:nowrap;color:'+(ar>0?'#16A34A':'#9CA3AF')+'">'+(ar>0?esc(pm.currency||'OMR')+' '+ar.toLocaleString():'\u2014')+'</td>'+'<td style="font-size:9px;font-weight:700;white-space:nowrap;color:'+vc+'">'+(ar>0?(v===0?'\u2713 On budget':(v>0?'\u25b2 Over '+Math.abs(v).toLocaleString():'\u25bc Under '+Math.abs(v).toLocaleString())):'\u2014')+'</td>'; })()
              +'<td><span class="pill" style="background:'+(pmC[pm.status]||'#eee')+'22;color:'+(pmC[pm.status]||'#888')+';border:1px solid '+(pmC[pm.status]||'#ccc')+'44">'+esc((pm.status||'').replace(/_/g,' '))+'</span></td>'
              +'<td style="font-size:11px">'+(pm.planned_date?fmt(pm.planned_date):'—')+'</td>'
              +(function(){var d=(pm.releaseLog&&pm.releaseLog.length>0?pm.releaseLog[pm.releaseLog.length-1].released_at:null)||pm.released_at;return '<td style="font-size:11px;color:#16A34A">'+(d?fmt(d.slice(0,10)):'—')+'</td>';})()
              +'</tr>').join('')
          + '</tbody></table></div>';
      }

      html += '<div class="ftr"><span>Generated by Enterprise Operations Management Platform (EOMP) &nbsp;·&nbsp; Space Communication Technologies</span><span>'+date+'</span></div></div></body></html>';

      if(pw&&!pw.closed){pw.document.open();pw.document.write(html);pw.document.close();}
    } catch(err){
      console.error('Full report error:',err);
      if(pw&&!pw.closed){pw.document.write('<body style="font-family:Arial;padding:24px;color:#DC2626"><h2>Report Error</h2><p>'+String(err.message)+'</p><p style="color:#666;font-size:12px;margin-top:8px">Check browser console for details.</p></body>');pw.document.close();}
    }
  }


  function exportOverviewSvg() {
    const name = space?.name||'Project';
    const el = document.getElementById('overview-container');
    if (!el) return;
    // Collect safe subset of styles — avoid full stylesheet dump which contains XML-unsafe chars
    const safeStyles = [
      'body{font-family:Arial,sans-serif;background:#F8F9FC;padding:24px;margin:0;font-size:12px;color:#1e1e2e}',
      '.card{background:#fff;border:1px solid #e0e0e0;border-radius:10px;padding:14px}',
      '*{box-sizing:border-box}',
    ].join('\n');
    const w = el.scrollWidth + 48, h = el.scrollHeight + 120;
    // Use CDATA to safely embed HTML/CSS in SVG — avoids & > < encoding issues
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xhtml="http://www.w3.org/1999/xhtml" width="' + w + '" height="' + h + '">'
      + '<foreignObject width="' + w + '" height="' + h + '">'
      + '<html xmlns="http://www.w3.org/1999/xhtml"><head><style>'
      + safeStyles
      + '</style></head><body>'
      + '<h2 style="margin:0 0 16px;font-size:18px;color:#1A1D23;font-family:Arial,sans-serif">' + name.replace(/&/g,'&amp;').replace(/</g,'&lt;') + ' Overview</h2>'
      + el.outerHTML
      + '</body></html></foreignObject></svg>';
    const blob = new Blob([svg], {type:'image/svg+xml'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'Overview_' + new Date().toISOString().slice(0,10) + '.svg';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }


  useEffect(() => { load(); }, [spaceId]);
  // Reload data when switching to time tab to ensure fresh data
  useEffect(() => { if (activeTab === 'timesheet' && spaceId) load(); }, [activeTab]);

  // ── Truly silent real-time sync — merges data in-place, zero flash
  useEffect(()=>{
    let lastTs = null;
    let active = true;
    let fails = 0;
    const poll = async()=>{
      if(!active||!spaceId) return;
      try {
        const r = await api.get(`/spaces/${spaceId}/last-updated`).catch(()=>null);
        if(!r){ fails++; if(fails>4) active=false; return; }
        fails=0;
        const hasChanged = lastTs === null || r.last_updated !== lastTs;
        lastTs = r.last_updated;
        // Always refresh — daysElapsed ticks daily, other data changes on update
        api.get(`/spaces/${spaceId}/dashboard`).then(fresh=>{
          if(!fresh||!active) return;
          setData(prev => fresh ? {...(prev||{}),
            stats: fresh.stats ?? prev?.stats,
            totalHours: fresh.totalHours ?? prev?.totalHours,
            byStatus: fresh.byStatus ?? prev?.byStatus,
            byPriority: fresh.byPriority ?? prev?.byPriority,
            byType: fresh.byType ?? prev?.byType,
            workload: fresh.workload ?? prev?.workload,
            openByAssignee: fresh.openByAssignee ?? prev?.openByAssignee,
            listsProgress: fresh.listsProgress ?? prev?.listsProgress,
            phasesProgress: fresh.phasesProgress ?? prev?.phasesProgress,
            overallProgress: fresh.overallProgress ?? prev?.overallProgress,
            paymentMilestones: fresh.paymentMilestones ?? prev?.paymentMilestones,
            daysElapsed: fresh.daysElapsed !== undefined ? fresh.daysElapsed : prev?.daysElapsed,
            projectStartDate: fresh.projectStartDate !== undefined ? fresh.projectStartDate : prev?.projectStartDate,
            statusOverTime: fresh.statusOverTime ?? prev?.statusOverTime,
            activity: fresh.activity ?? prev?.activity,
          } : fresh);
        }).catch(()=>{});
      } catch(_){ fails++; if(fails>4) active=false; }
    };
    const iv = setInterval(poll, 8000); // 8s for near real-time
    return ()=>{ active=false; clearInterval(iv); };
  },[spaceId]);


  async function load() {
    const emptyDash = { stats:{totalTasks:0,completedTasks:0,inProgressTasks:0,atRiskTasks:0,escalatedTasks:0,overdueTasks:0,completionRate:0}, byStatus:[], byPriority:[], byType:[], totalHours:{estimated:0,logged:0}, workload:[], openByAssignee:[], listsProgress:[], phasesProgress:[], pert:{tasks:[],dependencies:[]}, statusOverTime:[], overallProgress:0, daysElapsed:null, projectStartDate:null, paymentMilestones:[] };
    try {
      // Use allSettled so one failing request never blocks the others
      const [spaceRes, dashRes, foldersRes, usersRes] = await Promise.allSettled([
        api.get(`/spaces/${spaceId}`),
        api.get(`/spaces/${spaceId}/dashboard`),
        api.get(`/spaces/${spaceId}/folders`),
        api.get('/users'),
      ]);
      if (spaceRes.status==='fulfilled') setSpace(spaceRes.value);
      setData(dashRes.status==='fulfilled' ? dashRes.value : emptyDash);
      if (usersRes.status==='fulfilled') setAllUsers(usersRes.value);
      const foldersData = foldersRes.status==='fulfilled' ? (foldersRes.value||[]) : [];
      const fwl = await Promise.all(foldersData.map(async f => {
        const lists = await api.get(`/folders/${f.id}/lists`).catch(()=>[]);
        return { ...f, lists };
      }));
      setFolders(fwl);
    } catch(e) {
      console.error('Dashboard load error:', e);
      setData(emptyDash);
    }
  }

  async function createFolder() {
    if (!newFolder.name.trim()) return;
    // ALL phases count toward 100% — private ones still occupy their weight
    const allPhases    = foldersWithProgress || [];
    const visiblePhases = allPhases.filter(f => f.accessible !== false);
    const hiddenPhases  = allPhases.filter(f => f.accessible === false);
    const usedWeight   = allPhases.reduce((s,f)=>s+(f.weight||0),0);
    const hiddenWeight = hiddenPhases.reduce((s,f)=>s+(f.weight||0),0);
    const remaining    = Math.max(0, 100 - usedWeight);
    if (allPhases.length > 0 && remaining < 100) {
      // Show weight distribution — visible phases adjustable, hidden ones locked
      setWeightAdjustModal({
        type: 'phase',
        newItemName: newFolder.name,
        siblings: visiblePhases.map(f=>({id:f.id,name:f.name,weight:f.weight||0})),
        lockedSiblings: hiddenPhases.map(f=>({name:'🔒 Private Phase',weight:f.weight||0})),
        usedWeight,
        remaining,
        onConfirm: async (newWeight, adjustedSiblings) => {
          setWeightAdjustModal(null);
          for (const s of adjustedSiblings) {
            await api.put(`/folders/${s.id}`,{weight:s.weight}).catch(()=>{});
          }
          await api.post(`/spaces/${spaceId}/folders`,{...newFolder,weight:newWeight});
          setShowFolderModal(false);
          setNewFolder({name:'',description:'',color:'#5B5BD6',visibility:'inherited'});
          load(); if(ctx?.reloadSidebar) ctx.reloadSidebar(spaceId);
        },
        onCancel: ()=>setWeightAdjustModal(null),
      });
      return;
    }
    // No existing phases or all 0 — just create with default weight
    try {
      await api.post(`/spaces/${spaceId}/folders`,{...newFolder,weight:remaining||100});
      setShowFolderModal(false);
      setNewFolder({name:'',description:'',color:'#5B5BD6',visibility:'inherited'});
      load(); if(ctx?.reloadSidebar) ctx.reloadSidebar(spaceId);
    } catch(e){ console.error(e); }
  }

  async function createList() {
    if (!newList.name.trim()||!selectedFolder) return;
    // Check existing list weights in this phase
    const existingLists = foldersWithProgress.find(f=>f.id===selectedFolder.id)?.lists||[];
    const usedWeight = existingLists.reduce((s,l)=>s+(l.weight||0),0);
    const remaining = Math.max(0, 100 - usedWeight);
    if (existingLists.length > 0 && remaining < 100) {
      setWeightAdjustModal({
        type: 'list',
        newItemName: newList.name,
        siblings: existingLists.map(l=>({id:l.id,name:l.name,weight:l.weight||0})),
        usedWeight, remaining,
        onConfirm: async (newWeight, adjustedSiblings) => {
          setWeightAdjustModal(null);
          for (const s of adjustedSiblings) {
            await api.put(`/lists/${s.id}`,{weight:s.weight}).catch(()=>{});
          }
          await api.post(`/folders/${selectedFolder.id}/lists`,{...newList,weight:newWeight});
          setShowListModal(false);
          setNewList({name:'',description:'',color:'#5B5BD6'});
          load(); if(ctx?.reloadSidebar) ctx.reloadSidebar(spaceId);
        },
        onCancel: ()=>setWeightAdjustModal(null),
      });
      return;
    }
    try {
      await api.post(`/folders/${selectedFolder.id}/lists`,{...newList,weight:remaining||100});
      setShowListModal(false);
      setNewList({name:'',description:'',color:'#5B5BD6'});
      load(); if(ctx?.reloadSidebar) ctx.reloadSidebar(spaceId);
    } catch(e){ console.error(e); }
  }

  if (!data) return (
    <div className="page-enter" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)',gap:'10px',flexDirection:'column'}}>
      <div style={{width:16,height:16,border:'2px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
      Loading project dashboard…
    </div>
  );

  const maxWorkload = Math.max(...(data.workload||[]).map(w=>w.active_count||0),1);
  const donutSegments = (data.byStatus||[]).map(s=>({ label:STATUS_LABELS[s.status]||s.status, value:s.count, color:STATUS_COLORS[s.status]||'#8A93A6' }));
  const barData = (data.byStatus||[]).map(s=>({ label:(STATUS_LABELS[s.status]||s.status).slice(0,8), value:s.count, color:STATUS_COLORS[s.status]||'#8A93A6' }));

  // compute folder/phase progress — weighted contribution model
  const foldersWithProgress = folders.map(f => {
    const lists = f.lists||[];
    // Phase progress = Σ(list_weight × list_progress / 100)
    const phaseProgress = lists.reduce((s,l)=>s+(l.weight||0)*(l.progress||0)/100, 0);
    return { ...f, progress: Math.min(100,phaseProgress), listCount: lists.length, weight: f.weight||0 };
  });

  // ── Pre-compute overview chart data (avoids IIFEs in JSX)
  const typeSegs = (data?.byType||[]).map(t=>({
    label: t.task_type==='milestone'?'🏁 Milestone':t.task_type==='meeting'?'📅 Meeting':'📋 Task',
    value: t.count,
    color: t.task_type==='milestone'?'#D97706':t.task_type==='meeting'?'#2563EB':'#5B5BD6'
  }));
  const typeTotal = typeSegs.reduce((s,t)=>s+t.value,0);
  const hoursEst  = data?.totalHours?.estimated||0;
  const hoursLog  = data?.totalHours?.logged||0;
  const hoursPct  = hoursEst>0?Math.min(100,Math.round(hoursLog/hoursEst*100)):0;
  const hoursRem  = Math.max(0,hoursEst-hoursLog);
  const hoursCol  = hoursPct>=100?'#DC2626':hoursPct>=75?'#D97706':'#5B5BD6';
  const listSegs  = (data?.listsProgress||[]).map(l=>({label:l.name,value:Math.round(l.progress||0),color:l.color||'#5B5BD6'}));
  const listAvg   = listSegs.length>0?Math.round(listSegs.reduce((s,l)=>s+l.value,0)/listSegs.length):0;
  const assigneeColors = ['#5B5BD6','#D97706','#16A34A','#DC2626','#2563EB','#0891B2','#7C3AED','#DB2777','#059669','#B45309'];
  const openSegs  = (data?.openByAssignee||[]).map((a,i)=>({label:a.full_name,value:a.open_count,color:a.avatar_color||assigneeColors[i%assigneeColors.length],in_progress:a.in_progress_count,at_risk:a.at_risk_count,escalated:a.escalated_count}));
  const openTotal = openSegs.reduce((s,a)=>s+a.value,0);
  const wkldSegs  = (data?.workload||[]).map((w,i)=>({label:w.full_name,value:w.active_count,color:w.avatar_color||assigneeColors[i%assigneeColors.length],hours:w.total_hours}));
  const wkldTotal = wkldSegs.reduce((s,w)=>s+w.value,0);
  const priColors = {urgent:'#DC2626',high:'#D97706',normal:'#5B5BD6',low:'#8A93A6'};
  const priSegs   = (data?.byPriority||[]).map(p=>({label:p.priority.charAt(0).toUpperCase()+p.priority.slice(1),value:p.count,color:priColors[p.priority]||'#8A93A6'}));
  const priTotal  = priSegs.reduce((s,p)=>s+p.value,0);

  return (
    <div style={{flex:1,overflowY:'auto',background:'var(--bg)'}}>
      {toast && (
        <div style={{position:'fixed',bottom:'24px',left:'50%',transform:'translateX(-50%)',background:'#DC2626',color:'#fff',padding:'12px 20px',borderRadius:'10px',fontSize:'13px',fontWeight:600,boxShadow:'0 6px 24px rgba(0,0,0,0.2)',zIndex:9999,maxWidth:'90vw'}}>{toast}</div>
      )}

      {/* Project Header */}
      <div style={{background:'var(--surface)',borderBottom:'1px solid var(--border)',padding:'20px 28px'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{display:'flex',alignItems:'center',gap:'14px'}}>
            <div style={{width:'44px',height:'44px',borderRadius:'12px',background:(space?.color||'#5B5BD6')+'18',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'20px',fontWeight:'800',color:space?.color||'#5B5BD6',border:`2px solid ${space?.color||'#5B5BD6'}30`}}>
              {space?.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <h1 style={{fontSize:'20px',fontWeight:'800',color:'var(--text)',letterSpacing:'-0.5px',margin:0}}>{space?.name}</h1>
              {space?.description&&<p style={{fontSize:'12px',color:'var(--muted)',margin:'2px 0 0'}}>{space.description}</p>}
            </div>
          </div>
          {/* Action buttons */}
          <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
            <button className="btn btn-sm" onClick={()=>navigate(`/spaces/${spaceId}/calendar`)} title="Open the project calendar">
              📆 Calendar
            </button>
            {activeTab==='overview' && isOwnerOrPM && (
              <button className="btn btn-sm" onClick={()=>setShowWeeklyModal(true)} title="Configure the weekly email summary for this project">
                📅 Weekly Summary
              </button>
            )}
            {activeTab==='overview' && (
              <ReportDropdown
                onExec={exportExecutiveReport}
                onSummary={exportOverview}
                onDetail={()=>exportFullReport(true,false)}
                onFull={()=>exportFullReport(true,true)}
                onWorkbook={exportProjectWorkbook}
                onDeck={exportProjectDeck}
                showWorkbook={isOwnerOrPM}
                onBoardReport={downloadBoardReport}
              />
            )}
            <button className="btn btn-sm btn-primary" onClick={()=>setShowFolderModal(true)}>
              + Phase
            </button>
            {/* Permission button — always visible */}
            <button onClick={()=>setShowPermModal(true)}
              style={{display:'flex',alignItems:'center',gap:'6px',padding:'6px 12px',borderRadius:'var(--radius)',border:'1px solid var(--border2)',background:'var(--surface)',color:'var(--muted2)',fontSize:'12px',fontWeight:'600',cursor:'pointer',transition:'all .15s',boxShadow:'var(--shadow-sm)'}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='#5B5BD6';e.currentTarget.style.color='#5B5BD6';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border2)';e.currentTarget.style.color='var(--muted2)';}}>
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="2.5" y="6" width="8" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.2"/><path d="M4 6V4.5a2.5 2.5 0 015 0V6" stroke="currentColor" strokeWidth="1.2"/></svg>
              Permissions
            </button>
          </div>
        </div>

        {/* Tab navigation */}
        <div style={{display:'flex',gap:'0',marginTop:'16px',borderBottom:'2px solid var(--border)',marginBottom:'-2px',alignItems:'flex-end'}}>
          {[
            ['overview','◉ Overview'],
            ['phases','📋 Phases'],
            ['pert','🔗 PERT Chart'],
            ['timesheet','⏱ Timesheet'],
            ['gantt','📅 Gantt'],
            ['wbs','🔲 WBS'],
            ...(!isGuest ? [['resources','👥 Resources']] : []),
            ...(!isGuest ? [['payments','💰 Payments']] : []),
            ...(finAccess ? [['financial','💵 Financial']] : []),
            ...(riskAccess ? [['risks','⚠️ Risk Register']] : []),
            ['milestones','⭐ Major Milestones'],
          ].map(([tab,label])=>(
            <button key={tab} onClick={()=>{
              if(tab==='gantt') navigate(`/spaces/${spaceId}/gantt`);
              else if(tab==='wbs') navigate(`/spaces/${spaceId}/wbs`);
              else if(tab==='resources') navigate(`/spaces/${spaceId}/resources`);
              else if(tab==='payments') navigate(`/spaces/${spaceId}/payments`);
              else if(tab==='financial') navigate(`/spaces/${spaceId}/financial`);
              else if(tab==='risks') navigate(`/spaces/${spaceId}/risks`);
              else if(tab==='milestones') navigate(`/spaces/${spaceId}/milestones`);
              else if(tab==='calendar') navigate(`/spaces/${spaceId}/calendar`);
              else if(tab==='timesheet') navigate(`/spaces/${spaceId}/timesheet`);
              else setActiveTab(tab);
            }}
              style={{padding:'8px 16px',background:'none',border:'none',borderBottom:`2px solid ${activeTab===tab?'var(--brand)':'transparent'}`,color:activeTab===tab?'var(--brand)':'var(--muted)',fontWeight:activeTab===tab?'700':'500',fontSize:'13px',cursor:'pointer',transition:'all .15s',marginBottom:'-2px',whiteSpace:'nowrap'}}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{padding:'24px 28px'}}>

        {/* ── OVERVIEW TAB */}
        {activeTab==='overview' && (
          <div id="overview-container">
            {/* Row 1: KPI Cards — 7 cards including Days Elapsed */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'10px',marginBottom:'16px'}}>
              {[
                {label:'Total Tasks',  value:data.stats?.totalTasks||0,    sub:`${data.stats?.completionRate||0}% done`, icon:'📋',color:'var(--text)'},
                {label:'Completed',    value:data.stats?.completedTasks||0, sub:'finished',   icon:'✅',color:'#16A34A'},
                {label:'In Progress',  value:data.stats?.inProgressTasks||0,sub:'active now', icon:'⚡',color:'#5B5BD6'},
                {label:'At Risk',      value:data.stats?.atRiskTasks||0,    sub:'needs attention',icon:'⚠️',color:'#D97706'},
                {label:'Escalated',    value:data.stats?.escalatedTasks||0, sub:'requires action',icon:'🚨',color:'#DC2626'},
                {label:'Overdue',      value:data.stats?.overdueTasks||0,   sub:'past due date',  icon:'🔴',color:data.stats?.overdueTasks>0?'#DC2626':'#16A34A'},
                {label:'Days Running', value:(()=>{
                  const sd=data?.projectStartDate;
                  if(!sd) return '—';
                  // Ensure we use the date portion only, parse as local time
                  const datePart = String(sd).slice(0,10);
                  const [y,m,d] = datePart.split('-').map(Number);
                  const start = new Date(y, m-1, d); // local midnight — no timezone issues
                  const today = new Date(); today.setHours(0,0,0,0);
                  const days = Math.max(0, Math.floor((today - start) / (1000*60*60*24)));
                  return days;
                })(), sub:data.projectStartDate?'since '+(()=>{const sd=String(data.projectStartDate).slice(0,10);const[y,m,d]=sd.split('-');return `${d}/${m}/${y}`;})():'no tasks yet',icon:'📅',color:'#0891B2'},
              ].map(k=>(<StatCard key={k.label} label={k.label} value={k.value} sub={k.sub} icon={k.icon} color={k.color}/>))}
            </div>

            {/* Row 2: Status Donut + Task Types + Hours */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1.4fr',gap:'14px',marginBottom:'14px'}}>
              <div className="card" style={{padding:'18px'}}>
                <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'12px'}}>Tasks by Status</div>
                <DonutChart segments={donutSegments} size={120} label={`${data.stats?.completionRate||0}%`}/>
                <div style={{marginTop:'12px',display:'flex',flexDirection:'column',gap:'5px'}}>
                  {donutSegments.filter(s=>s.value>0).slice(0,5).map(s=>(
                    <div key={s.label} style={{display:'flex',alignItems:'center',gap:'7px',fontSize:'11px'}}>
                      <div style={{width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0}}/>
                      <span style={{flex:1,color:'var(--muted2)'}}>{s.label}</span>
                      <span style={{fontWeight:'700',color:'var(--text)'}}>{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{padding:'18px'}}>
                <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'12px'}}>Task Types</div>
                <DonutChart segments={typeSegs} size={120} label={`${typeTotal}`}/>
                <div style={{marginTop:'12px',display:'flex',flexDirection:'column',gap:'5px'}}>
                  {typeSegs.map(s=>(
                    <div key={s.label} style={{display:'flex',alignItems:'center',gap:'7px',fontSize:'11px'}}>
                      <div style={{width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0}}/>
                      <span style={{flex:1,color:'var(--muted2)'}}>{s.label}</span>
                      <span style={{fontWeight:'700',color:'var(--text)'}}>{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{padding:'18px',flex:1}}>
                <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'10px'}}>Project Hours</div>
                {hoursEst===0
                  ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>Set time budgets on tasks to track hours</div>
                  : (()=>{
                      const pct=Math.min(100,hoursEst>0?Math.round((hoursLog/hoursEst)*100):0);
                      const col=pct>90?'#DC2626':pct>70?'#D97706':'#5B5BD6';
                      const r=52,cx=80,cy=68,sw=12;
                      const circ=2*Math.PI*r;
                      const halfCirc=circ/2;
                      return (
                        <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                          <div style={{position:'relative',width:160,height:90}}>
                            <svg width={160} height={90} viewBox="0 0 160 90">
                              <path d={`M ${cx-r},${cy} A ${r},${r} 0 0 1 ${cx+r},${cy}`} fill="none" stroke="var(--surface3)" strokeWidth={sw} strokeLinecap="round"/>
                              <path d={`M ${cx-r},${cy} A ${r},${r} 0 0 1 ${cx+r},${cy}`} fill="none" stroke={col} strokeWidth={sw} strokeLinecap="round"
                                strokeDasharray={halfCirc} strokeDashoffset={halfCirc*(1-pct/100)}
                                style={{transition:'stroke-dashoffset .6s ease'}}/>
                            </svg>
                            <div style={{position:'absolute',bottom:0,left:0,right:0,textAlign:'center'}}>
                              <div style={{fontSize:'22px',fontWeight:'900',color:col,lineHeight:1}}>{pct}%</div>
                              <div style={{fontSize:'10px',color:'var(--muted)',fontWeight:'600'}}>of budget used</div>
                            </div>
                          </div>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'7px',width:'100%',marginTop:'10px'}}>
                            {[
                              {l:'Budgeted', v:`${hoursEst}h`, col:'var(--muted2)'},
                              {l:'Logged',   v:`${hoursLog}h`, col},
                              {l:'Remaining',v:`${Math.max(0,hoursEst-hoursLog).toFixed(1)}h`, col:'#16A34A'},
                              hoursLog>hoursEst
                                ? {l:'Overrun', v:`+${(hoursLog-hoursEst).toFixed(1)}h`, col:'#DC2626'}
                                : {l:'On Track', v:`${pct}% used`, col:'#16A34A'},
                            ].map(s=>(
                              <div key={s.l} style={{background:s.l==='Overrun'?'rgba(220,38,38,0.08)':'var(--surface2)',border:s.l==='Overrun'?'1px solid rgba(220,38,38,0.3)':'none',borderRadius:'var(--radius)',padding:'7px 10px'}}>
                                <div style={{fontSize:'10px',color:s.l==='Overrun'?'#DC2626':'var(--muted)',fontWeight:'600'}}>{s.l}</div>
                                <div style={{fontSize:'14px',fontWeight:'800',color:s.col}}>{s.v}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()
                }
              </div>
            </div>

            {/* Rows 3: List Progress + Hours + Payments with 3 aligned right capsules */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 155px',gap:'14px',marginBottom:'14px'}}>

              {/* LEFT: 3 stacked content cards */}
              <div style={{display:'flex',flexDirection:'column',gap:'14px',minWidth:0,overflow:'hidden'}}>

                {/* List Progress — with Phase Filter */}
                <div className="card" style={{padding:'18px',flex:1}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'10px',flexWrap:'wrap',gap:'8px'}}>
                    <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)'}}>List Progress by Phase</div>
                    {/* Phase filter pills */}
                    <div style={{display:'flex',gap:'4px',flexWrap:'wrap'}}>
                      <button onClick={()=>setOverviewPhaseFilter('all')}
                        style={{padding:'2px 10px',borderRadius:'99px',border:`1.5px solid ${overviewPhaseFilter==='all'?'var(--brand)':'var(--border)'}`,background:overviewPhaseFilter==='all'?'var(--brand-dim)':'transparent',color:overviewPhaseFilter==='all'?'var(--brand)':'var(--muted)',fontSize:'10px',fontWeight:'600',cursor:'pointer',transition:'all .15s'}}>
                        All
                      </button>
                      {(foldersWithProgress||[]).map(f=>(
                        <button key={f.id} onClick={()=>setOverviewPhaseFilter(f.id)}
                          style={{padding:'2px 10px',borderRadius:'99px',border:`1.5px solid ${overviewPhaseFilter===f.id?(f.color||'var(--brand)'):'var(--border)'}`,background:overviewPhaseFilter===f.id?(f.color||'var(--brand)')+'18':'transparent',color:overviewPhaseFilter===f.id?(f.color||'var(--brand)'):'var(--muted)',fontSize:'10px',fontWeight:'600',cursor:'pointer',transition:'all .15s',maxWidth:'100px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {f.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(() => {
                    // Determine which lists to show based on phase filter
                    let filtered = [];
                    if (overviewPhaseFilter === 'all') {
                      filtered = (data?.listsProgress||[]);
                    } else {
                      const folder = foldersWithProgress.find(f=>f.id===overviewPhaseFilter);
                      filtered = (folder?.lists||[]).map(l=>({
                        id:l.id, name:l.name,
                        progress:Math.round(l.progress||0),
                        color:l.color||folder.color||'#5B5BD6'
                      }));
                    }
                    if (filtered.length === 0) return (
                      <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No lists in this phase</div>
                    );
                    return (
                      <div style={{
                        display:'flex', gap:'10px', alignItems:'flex-start',
                        overflowX:'auto', overflowY:'hidden',
                        paddingBottom:'8px', paddingTop:'4px',
                        scrollbarWidth:'thin', scrollbarColor:'var(--border) transparent',
                      }}>
                        {filtered.map(l => {
                          const pct = Math.round(l.progress||0);
                          const col = pct>=100?'#16A34A':pct>=60?(l.color||'#5B5BD6'):pct>=30?'#D97706':'#DC2626';
                          return (
                            <div key={l.id||l.name} title={l.name}
                              style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0,width:'64px'}}>
                              {/* ZONE 1: pct — fixed 22px */}
                              <div style={{height:'22px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <span style={{fontSize:'11px',fontWeight:'800',color:col}}>{pct}%</span>
                              </div>
                              {/* ZONE 2: spacer — fixed 18px (keeps tubes aligned with hours/payment) */}
                              <div style={{height:'18px',flexShrink:0}}/>
                              {/* ZONE 3: TUBE — fixed 110px, NEVER changes size */}
                              <div style={{width:'32px',height:'110px',flexShrink:0,background:'var(--surface3)',borderRadius:'10px',overflow:'hidden',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
                                <div style={{width:'100%',height:Math.max(pct,0)+'%',background:col,borderRadius:'10px',transition:'height .5s ease',minHeight:pct>0?'3px':'0'}}/>
                              </div>
                              {/* ZONE 4: name text — wraps freely below tube */}
                              <div style={{marginTop:'6px',width:'64px',textAlign:'center',flexShrink:0}}>
                                <span style={{fontSize:'9px',color:'var(--muted2)',fontWeight:'600',lineHeight:'1.4',wordBreak:'break-word',display:'block'}}>{l.name}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Hours Progress */}
                <div className="card" style={{padding:'18px'}}>
                  <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'14px'}}>Hours Progress</div>
                  {(data?.listsProgress||[]).length===0
                    ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No lists yet</div>
                    : <div style={{display:'flex',gap:'10px',alignItems:'flex-start',overflowX:'auto',overflowY:'hidden',paddingBottom:'8px',scrollbarWidth:'thin',scrollbarColor:'var(--border) transparent'}}>
                        {(data?.listsProgress||[]).map(l=>{
                          const pct = l.budgetHours>0?Math.min(100,Math.round((l.loggedHours/l.budgetHours)*100)):0;
                          const over = l.budgetHours>0&&l.loggedHours>l.budgetHours;
                          const nobudget = l.budgetHours===0;
                          const col = nobudget?'#CBD5E1':over?'#DC2626':pct>=80?'#D97706':'#16A34A';
                          return (
                            <div key={l.id} title={l.name}
                              style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0,width:'64px'}}>
                              {/* ZONE 1: pct — fixed 22px */}
                              <div style={{height:'22px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <span style={{fontSize:'11px',fontWeight:'800',color:col,textAlign:'center'}}>{nobudget?'—':over?'⚠'+pct+'%':pct+'%'}</span>
                              </div>
                              {/* ZONE 2: sub-label — fixed 18px */}
                              <div style={{height:'18px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <span style={{fontSize:'8px',color:'var(--muted)',textAlign:'center',lineHeight:'1.2'}}>{nobudget?'No budget':l.budgetHours+'h'}</span>
                              </div>
                              {/* ZONE 3: TUBE — fixed 110px, NEVER changes */}
                              <div style={{position:'relative',width:'32px',height:'110px',flexShrink:0,background:'var(--surface3)',borderRadius:'10px',overflow:'visible',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
                                <div style={{width:'100%',height:Math.min(100,pct)+'%',background:col,borderRadius:'10px',transition:'height .5s ease',minHeight:pct>0?'3px':'0'}}/>
                                {over&&<div style={{position:'absolute',top:'-8px',right:'-8px',background:'#DC2626',color:'#fff',fontSize:'7px',fontWeight:'900',borderRadius:'4px',padding:'1px 3px',whiteSpace:'nowrap',zIndex:2}}>+{(l.loggedHours-l.budgetHours).toFixed(1)}h</div>}
                              </div>
                              {/* ZONE 4: name + logged below tube */}
                              <div style={{marginTop:'6px',width:'64px',textAlign:'center',flexShrink:0}}>
                                <span style={{fontSize:'9px',color:'var(--muted2)',fontWeight:'600',lineHeight:'1.4',wordBreak:'break-word',display:'block'}}>{l.name}</span>
                                <div style={{fontSize:'8px',color:col,fontWeight:'700',marginTop:'2px'}}>{nobudget?'—':over?'+'+((l.loggedHours-l.budgetHours).toFixed(1))+'h':l.loggedHours+'h'}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                  }
                </div>

                {/* Payment Milestones — only for Project Managers */}
                {data?.isProjectPM && <div className="card" style={{padding:'18px'}}>
                  <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'14px'}}>Payment Milestones</div>
                  {(data?.paymentMilestones||[]).length===0
                    ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No payment milestones yet</div>
                    : <div style={{display:'flex',gap:'10px',alignItems:'flex-start',overflowX:'auto',overflowY:'hidden',paddingBottom:'8px',scrollbarWidth:'thin',scrollbarColor:'var(--border) transparent'}}>
                        {(data?.paymentMilestones||[]).map(pm=>{
                          const _pmAR=(pm.releaseLog||[]).reduce((s,r)=>s+parseFloat(r.amount||0),0);
                          const _pmPl=parseFloat(pm.amount||0);
                          const _pmPct=_pmPl>0?Math.min(110,Math.round(_pmAR/_pmPl*100)):0;
                          const pct=_pmAR>0?Math.min(110,_pmPct):Math.min(100,Math.round(pm.avg_progress||0));
                          const _pmIsOver=_pmAR>_pmPl&&_pmPl>0;
                          const isReady=pm.status==='ready_for_release';
                          const isReleased=pm.status==='released';
                          const isUnderReview=pm.status==='under_review';
                          const col=_pmIsOver?'#B45309':isReleased?'#16A34A':isUnderReview?'#B45309':isReady?'#059669':pct>=90?'#D97706':'#5B5BD6';
                          const stBg=isReleased?'#D1FAE5':isUnderReview?'#FEF3C7':isReady?'#D1FAE5':pct>=90?'#FEF3C7':'#EEF2FF';
                          const stCol=isReleased?'#065F46':isUnderReview?'#92400E':isReady?'#059669':pct>=90?'#92400E':'#4338CA';
                          return (
                            <div key={pm.id} title={pm.name}
                              style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0,width:'64px'}}>
                              {/* ZONE 1: pct — fixed 22px */}
                              <div style={{height:'22px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <span style={{fontSize:'11px',fontWeight:'800',color:col,textAlign:'center'}}>{_pmAR>0?(pct+'%'):(Math.round(pm.avg_progress||0)+'%')}</span>
                              </div>
                              {/* ZONE 2: amount sub-label — fixed 18px */}
                              <div style={{height:'18px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <span style={{fontSize:'8px',color:_pmIsOver?'#B45309':_pmAR>0?'#16A34A':'var(--muted)',textAlign:'center',lineHeight:'1.2',fontWeight:'700'}}>{pm.currency||'OMR'} {_pmAR>0?_pmAR.toLocaleString():Number(pm.amount).toLocaleString()}</span>
                              </div>
                              {/* ZONE 3: TUBE — fixed 110px, NEVER changes */}
                              <div style={{position:'relative',width:'32px',height:'110px',flexShrink:0,background:'var(--surface3)',borderRadius:'10px',overflow:'hidden',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
                                <div style={{width:'100%',height:Math.min(100,pct)+'%',background:col,borderRadius:'10px',transition:'height .5s ease',minHeight:pct>0?'3px':'0'}}/>
                                {isReady&&<div style={{position:'absolute',top:0,left:0,right:0,bottom:0,border:'2px solid #059669',borderRadius:'10px',animation:'pulse 1.5s ease-in-out infinite'}}/>}
                                {isUnderReview&&<div style={{position:'absolute',top:0,left:0,right:0,bottom:0,border:'2px dashed #B45309',borderRadius:'10px',animation:'pulse 1s ease-in-out infinite'}}/>}
                              </div>
                              {/* ZONE 4: name + status below tube */}
                              <div style={{marginTop:'6px',width:'64px',textAlign:'center',flexShrink:0}}>
                                <span style={{fontSize:'9px',color:'var(--muted2)',fontWeight:'600',lineHeight:'1.4',wordBreak:'break-word',display:'block'}}>{pm.name}</span>
                                <div style={{marginTop:'3px',display:'inline-block',fontSize:'7px',padding:'2px 5px',borderRadius:'99px',fontWeight:'700',background:stBg,color:stCol,whiteSpace:'nowrap',border:isUnderReview?`1px solid #B45309`:_pmIsOver?'1px solid #B45309':'none'}}>{_pmIsOver?'▲ Over':isReleased?'✓ Released':isUnderReview?'⚠ Review':isReady?'💰 Ready':'Pending'}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                  }
                </div>}

              </div>

              {/* RIGHT: 3 vertically aligned capsules */}
              <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>

                {/* Capsule 1: Overall Progress */}
                {(()=>{
                  const pctNum=Number(data?.overallProgress ?? data?.stats?.completionRate ?? 0); const pct=d1(pctNum);
                  const col=pctNum>=100?'#16A34A':pctNum>=60?'#0891B2':pctNum>=30?'#D97706':'#DC2626';
                  return (
                    <div className="card" style={{padding:'14px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flex:1}}>
                      <div style={{fontSize:'9px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted2)',marginBottom:'8px',textAlign:'center'}}>Project Completion</div>
                      <div style={{position:'relative',width:'52px',height:'100px'}}>
                        <div style={{position:'absolute',inset:0,border:'3px solid #CBD5E1',borderRadius:'26px',background:'var(--surface3)',overflow:'hidden'}}>
                          <div style={{position:'absolute',bottom:0,left:0,right:0,height:pctNum+'%',background:col,transition:'height .6s ease',opacity:.85}}/>
                        </div>
                        <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <span style={{fontSize:'11px',fontWeight:'800',color:pctNum>40?'#fff':col,textShadow:pctNum>40?'0 1px 2px rgba(0,0,0,.3)':'none'}}>{pct}%</span>
                        </div>
                      </div>
                      <span style={{fontSize:'9px',color:'var(--muted)',fontWeight:'600',textAlign:'center',marginTop:'6px'}}>Overall Progress</span>
                    </div>
                  );
                })()}

                {/* Capsule 2: Total Hours */}
                {(()=>{
                  const tb=hoursEst; const tl=hoursLog;
                  const op=tb>0?Math.min(110,Math.round((tl/tb)*100)):0;
                  const oo=tb>0&&tl>tb;
                  const col=tb===0?'#CBD5E1':oo?'#DC2626':op>=80?'#D97706':'#16A34A';
                  return (
                    <div className="card" style={{padding:'14px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flex:1}}>
                      <div style={{fontSize:'9px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted2)',marginBottom:'4px',textAlign:'center'}}>{oo?'⚠ Over Budget':'Total Hours'}</div>
                      <div style={{fontSize:'8px',color:'var(--muted)',marginBottom:'6px',textAlign:'center'}}>{tb}h budget</div>
                      <div style={{position:'relative',width:'52px',height:'100px'}}>
                        <div style={{position:'absolute',inset:0,border:'3px solid #CBD5E1',borderRadius:'26px',background:'var(--surface3)',overflow:'hidden'}}>
                          <div style={{position:'absolute',bottom:0,left:0,right:0,height:Math.min(100,op)+'%',background:col,transition:'height .6s ease',opacity:.85}}/>
                        </div>
                        <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <span style={{fontSize:'11px',fontWeight:'800',color:op>40?'#fff':col,textShadow:op>40?'0 1px 2px rgba(0,0,0,.3)':'none'}}>{tb===0?'—':op+'%'}</span>
                        </div>
                        {oo&&<div style={{position:'absolute',top:'-8px',right:'-6px',background:'#DC2626',color:'#fff',fontSize:'7px',fontWeight:'900',borderRadius:'4px',padding:'1px 3px',whiteSpace:'nowrap',zIndex:2}}>+{(tl-tb).toFixed(1)}h</div>}
                      </div>
                      <span style={{fontSize:'9px',fontWeight:'700',color:col,textAlign:'center',marginTop:'6px'}}>{tl}h logged</span>
                      {!oo&&tb>0&&<span style={{fontSize:'8px',color:'var(--muted)',marginTop:'2px'}}>{Math.round((tb-tl)*10)/10}h left</span>}
                    </div>
                  );
                })()}

                {/* Capsule 3: Budget Released — PM only */}
                {data?.isProjectPM && (()=>{
                  const pms = data?.paymentMilestones||[];
                  // Group by currency for multi-currency support
                  const byCur = {};
                  pms.forEach(m=>{
                    const cur=m.currency||'OMR';
                    if(!byCur[cur]) byCur[cur]={total:0,released:0};
                    byCur[cur].total += parseFloat(m.amount||0);
                    const _g=(m.releaseLog||[]).reduce((s,r)=>s+parseFloat(r.amount||0),0);byCur[cur].released+=(_g>0?_g:(m.status==='released'?parseFloat(m.amount||0):0));
                  });
                  const currencies = Object.keys(byCur);
                  const totalOverall = pms.length;
                  const releasedCount = pms.filter(m=>m.status==='released').length;
                  const _tc=pms.reduce((s,m)=>s+parseFloat(m.amount||0),0);
                  const _tar=pms.reduce((s,m)=>{const ar=(m.releaseLog||[]).reduce((rs,r)=>rs+parseFloat(r.amount||0),0);return s+(ar>0?ar:(m.status==='released'?parseFloat(m.amount||0):0));},0);
                  const bp=_tc>0?Math.min(110,Math.round(_tar/_tc*100)):0;
                  const _isOver=_tar>_tc&&_tc>0;
                  const col=_isOver?'#B45309':bp>=100?'#16A34A':bp>=60?'#0891B2':bp>=30?'#D97706':'#DC2626';
                  return (
                    <div className="card" style={{padding:'14px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flex:1}}>
                      <div style={{fontSize:'9px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted2)',marginBottom:'4px',textAlign:'center'}}>Budget Released</div>
                      <div style={{position:'relative',width:'52px',height:'80px'}}>
                        <div style={{position:'absolute',inset:0,border:'3px solid #CBD5E1',borderRadius:'26px',background:'var(--surface3)',overflow:'hidden'}}>
                          <div style={{position:'absolute',bottom:0,left:0,right:0,height:bp+'%',background:col,transition:'height .6s ease',opacity:.85}}/>
                        </div>
                        <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <span style={{fontSize:'11px',fontWeight:'800',color:bp>40?'#fff':col,textShadow:bp>40?'0 1px 2px rgba(0,0,0,.3)':'none'}}>{totalOverall===0?'—':bp+'%'}</span>
                        </div>
                      </div>
                      <span style={{fontSize:'9px',fontWeight:'700',color:col,textAlign:'center',marginTop:'6px'}}>{releasedCount}/{totalOverall} released</span>
                      <span style={{fontSize:'8px',color:_isOver?'#B45309':'var(--muted)',textAlign:'center',marginTop:'2px',display:'block'}}>{_tc>0?currencies[0]+' '+Math.round(_tar).toLocaleString()+' of '+Math.round(_tc).toLocaleString():''}</span>
                      {_isOver&&<span style={{fontSize:'8px',color:'#B45309',fontWeight:'800',textAlign:'center',display:'block'}}>Over-released</span>}
                      {currencies.length<=1 ? (
                        <div style={{marginTop:'4px',textAlign:'center'}}>
                          {currencies.map(cur=>(
                            <div key={cur} style={{fontSize:'8px',color:'var(--muted)',lineHeight:'1.5'}}>
                              <span style={{color:col,fontWeight:'700'}}>{cur} {byCur[cur].released.toLocaleString()}</span>
                              <span style={{color:'var(--muted)'}}> of {byCur[cur].total.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{marginTop:'4px',width:'100%'}}>
                          {currencies.map(cur=>(
                            <div key={cur} style={{fontSize:'8px',display:'flex',justifyContent:'space-between',alignItems:'center',padding:'1px 2px'}}>
                              <span style={{color:'var(--muted)',fontWeight:'600'}}>{cur}</span>
                              <span style={{color:byCur[cur].released>=byCur[cur].total&&byCur[cur].total>0?'#16A34A':col,fontWeight:'700'}}>{byCur[cur].released.toLocaleString()}</span>
                              <span style={{color:'var(--muted)',fontSize:'7px'}}>/{byCur[cur].total.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            </div>

            {/* Row 4: Team Workload + Open Tasks by Assignee + Priority */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'14px',marginBottom:'14px'}}>
              <div className="card" style={{padding:'18px'}}>
                <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'14px'}}>Team Workload</div>
                {wkldSegs.length===0
                  ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No assigned tasks yet</div>
                  : <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                      {wkldSegs.map((s,i)=>{
                        const maxV=Math.max(...wkldSegs.map(x=>x.value),1);
                        const maxH=Math.max(...wkldSegs.map(x=>x.hours||0),1);
                        return (
                          <div key={s.label}>
                            <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'4px'}}>
                              <Avatar name={s.label} color={s.color} size={22}/>
                              <span style={{flex:1,fontSize:'11px',fontWeight:'600',color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.label}</span>
                              <span style={{fontSize:'10px',color:'var(--muted)',fontWeight:'600'}}>{Math.round(s.hours||0)}h · {s.value} task{s.value!==1?'s':''}</span>
                            </div>
                            <div style={{display:'flex',gap:'3px'}}>
                              <div style={{flex:1,height:'6px',background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                                <div style={{height:'100%',width:((s.value/maxV)*100)+'%',background:s.color,borderRadius:'99px',transition:'width .5s'}}/>
                              </div>
                              <div style={{flex:1,height:'6px',background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                                <div style={{height:'100%',width:((s.hours||0)/maxH*100)+'%',background:s.color+'80',borderRadius:'99px',transition:'width .5s'}}/>
                              </div>
                            </div>
                            <div style={{display:'flex',justifyContent:'space-between',fontSize:'9px',color:'var(--muted)',marginTop:'2px'}}><span>Tasks</span><span>Hours</span></div>
                          </div>
                        );
                      })}
                    </div>
                }
              </div>

              <div className="card" style={{padding:'18px'}}>
                <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'12px'}}>Open Tasks by Assignee</div>
                {openSegs.length===0
                  ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No assigned open tasks</div>
                  : <OpenTasksBars segs={openSegs}/>
                }
              </div>

              <div className="card" style={{padding:'18px'}}>
                <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'12px'}}>Priority Breakdown</div>
                {priSegs.length===0
                  ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No tasks yet</div>
                  : (()=>{
                      // Clean arc-ring chart (like reference image 2 — smooth arcs per segment)
                      const cx=54, cy=54, R=42, strokeW=10;
                      const innerR=R-strokeW/2;
                      const circumference=2*Math.PI*innerR;
                      // Calculate arc offsets for each priority
                      let offset=0;
                      const arcs=priSegs.map(s=>{
                        const frac=priTotal>0?s.value/priTotal:0;
                        const len=frac*circumference*0.98; // 0.98 leaves tiny gap between arcs
                        const arc={color:s.color, offset, len, frac};
                        offset+=frac*circumference;
                        return arc;
                      });
                      return (
                        <div style={{display:'flex',gap:'14px',alignItems:'center'}}>
                          <div style={{flexShrink:0,filter:'drop-shadow(0 2px 8px rgba(0,0,0,0.08))'}}>
                            <svg width="108" height="108" viewBox="0 0 108 108">
                              {/* Track */}
                              <circle cx={cx} cy={cy} r={innerR} fill="none"
                                stroke="var(--surface3)" strokeWidth={strokeW}/>
                              {/* Colored arcs */}
                              {arcs.map((a,i)=>(
                                <circle key={i} cx={cx} cy={cy} r={innerR} fill="none"
                                  stroke={a.color} strokeWidth={strokeW}
                                  strokeDasharray={`${a.len} ${circumference}`}
                                  strokeDashoffset={-a.offset}
                                  strokeLinecap="round"
                                  style={{transform:'rotate(-90deg)',transformOrigin:`${cx}px ${cy}px`,transition:'stroke-dasharray .6s ease'}}
                                />
                              ))}
                              {/* Center */}
                              <circle cx={cx} cy={cy} r={innerR-strokeW/2-2} fill="var(--bg)"/>
                              <text x={cx} y={cy-5} textAnchor="middle" dominantBaseline="middle"
                                fontSize="18" fontWeight="900" fill="#1E1E3F">{priTotal}</text>
                              <text x={cx} y={cy+10} textAnchor="middle" dominantBaseline="middle"
                                fontSize="8" fontWeight="700" fill="#8A93A6" letterSpacing="1">TASKS</text>
                            </svg>
                          </div>
                          <div style={{flex:1,display:'flex',flexDirection:'column',gap:'8px'}}>
                            {priSegs.map(s=>{
                              const pct=priTotal>0?Math.round(s.value/priTotal*100):0;
                              return (
                                <div key={s.label} style={{padding:'6px 10px',borderRadius:'8px',background:'var(--surface2)',border:`1px solid ${s.color}40`}}>
                                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'4px'}}>
                                    <span style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'11px',fontWeight:'700',color:'var(--text)'}}>
                                      <div style={{width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0,boxShadow:`0 0 0 2px ${s.color}40`}}/>
                                      {s.label}
                                    </span>
                                    <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                                      <span style={{fontSize:'10px',color:'var(--muted)',fontWeight:'600'}}>{pct}%</span>
                                      <span style={{fontWeight:'800',fontSize:'13px',color:s.color}}>{s.value}</span>
                                    </div>
                                  </div>
                                  <div style={{height:5,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                                    <div style={{height:'100%',width:pct+'%',background:s.color,borderRadius:'99px',transition:'width .5s'}}/>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
              </div>
            </div>


          </div>
        )}

        {/* ── PHASES TAB */}
        {activeTab==='phases' && (
          <div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                <h3 style={{fontSize:'15px',fontWeight:'700',color:'var(--text)',margin:0}}>Project Phases</h3>
                {foldersWithProgress.length>0 && (() => {
                  const totW = foldersWithProgress.reduce((s,f)=>s+(f.weight||0),0);
                  const ok = totW === 100;
                  return (
                    <span title={ok?'Phase weights add up to 100%':'Phase weights don’t total 100% yet — the remaining amount is shown. Roll-up uses the weights as set.'}
                      style={{fontSize:'11px',fontWeight:'700',padding:'3px 10px',borderRadius:'99px',
                        color: ok ? '#16A34A' : '#D97706',
                        background: ok ? 'rgba(22,163,74,0.10)' : 'rgba(217,119,6,0.12)',
                        border: '1px solid '+(ok ? 'rgba(22,163,74,0.30)' : 'rgba(217,119,6,0.35)')}}>
                      ⚖ Total weight: {totW}%{ok ? '' : (totW < 100 ? ` — ${Math.round((100 - totW) * 10) / 10}% remaining` : ` — ${Math.round((totW - 100) * 10) / 10}% over`)}
                    </span>
                  );
                })()}
              </div>
              {canManage && <button className="btn btn-sm btn-primary" onClick={()=>setShowFolderModal(true)}>+ New Phase</button>}
            </div>
            {foldersWithProgress.length===0
              ? <div style={{textAlign:'center',padding:'60px',color:'var(--muted)',border:'1px dashed var(--border2)',borderRadius:'var(--radius-xl)'}}>
                  <div style={{fontSize:'32px',marginBottom:'12px'}}>📁</div>
                  <p>No phases yet. Create a phase to organize your project.</p>
                </div>
              : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:'16px'}}>
                  {foldersWithProgress.map(folder=>{
                    const pc = pColor(folder.progress);
                    return (
                      <div key={folder.id} className="card" style={{padding:'20px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'14px'}}>
                          <div style={{width:10,height:10,borderRadius:'2px',background:folder.color||'#5B5BD6',flexShrink:0}}/>
                          <span style={{fontSize:'14px',fontWeight:'700',color:'var(--text)',flex:1}}>{folder.name}</span>
                          <span title="Phase weight — its share of the project's total progress"
                            style={{fontSize:'10px',fontWeight:'800',padding:'2px 8px',borderRadius:'99px',
                              color: folder.weight>0 ? '#5B5BD6' : '#DC2626',
                              background: folder.weight>0 ? 'rgba(91,91,214,0.10)' : 'rgba(220,38,38,0.10)',
                              border: '1px solid '+(folder.weight>0 ? 'rgba(91,91,214,0.30)' : 'rgba(220,38,38,0.30)'),
                              whiteSpace:'nowrap'}}>
                            ⚖ {folder.weight||0}%
                          </span>
                          <span style={{fontSize:'12px',fontWeight:'700',color:pc}}>{Math.round(folder.progress)}%</span>
                          {/* Phase permission button */}
                          <button title="Phase Permissions" onClick={()=>setPhasePermModal(folder.id)} style={{background:'none',border:'1px solid var(--border)',borderRadius:'6px',padding:'3px 6px',cursor:'pointer',color:'var(--muted)',display:'flex',alignItems:'center',transition:'all .12s'}}
                            onMouseEnter={e=>{e.currentTarget.style.borderColor='#5B5BD6';e.currentTarget.style.color='#5B5BD6';}}
                            onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--muted)';}}>
                            <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><rect x="2" y="5" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="1.2"/><path d="M3.5 5V3.5a2 2 0 014 0V5" stroke="currentColor" strokeWidth="1.2"/></svg>
                          </button>
                        </div>
                        <div style={{height:8,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden',marginBottom:'14px'}}>
                          <div style={{height:'100%',width:`${Math.min(100,folder.progress)}%`,background:pc,borderRadius:'99px',transition:'width .5s ease'}}/>
                        </div>
                        <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'12px'}}>
                          {folder.lists?.map(lst=>(
                            <div key={lst.id} style={{display:'flex',alignItems:'center',gap:'2px',background:'var(--surface2)',borderRadius:'99px',border:'1px solid var(--border)',overflow:'hidden'}}>
                              <span onClick={()=>navigate(`/lists/${lst.id}`)}
                                style={{fontSize:'11px',padding:'3px 6px',color:'var(--muted2)',cursor:'pointer',transition:'all .12s'}}
                                onMouseEnter={e=>{e.currentTarget.style.color='var(--brand)';}}
                                onMouseLeave={e=>{e.currentTarget.style.color='var(--muted2)';}}>
                                {lst.name} <span style={{opacity:.6}}>{Math.round(lst.progress||0)}%</span>
                              </span>
                              <span title="List weight" style={{fontSize:'9px',fontWeight:'700',padding:'1px 5px',color:'var(--muted)',borderLeft:'1px solid var(--border)',paddingLeft:'5px'}}>{lst.weight||0}%</span>
                              <button title="Delete list"
                                onClick={(e)=>{
                                  e.stopPropagation();
                                  setDeleteConfirm({
                                    msg: `Delete list "${lst.name}" and ALL its tasks? This cannot be undone.`,
                                    onConfirm: async () => {
                                      try { await api.delete(`/lists/${lst.id}`); load(); if(ctx?.reloadSidebar) ctx.reloadSidebar(spaceId); }
                                      catch(err){ console.error(err); }
                                    }
                                  });
                                }}
                                style={{background:'none',border:'none',cursor:'pointer',color:'var(--muted)',padding:'3px 6px',fontSize:'11px',transition:'color .1s'}}
                                onMouseEnter={e=>e.currentTarget.style.color='var(--red)'}
                                onMouseLeave={e=>e.currentTarget.style.color='var(--muted)'}>×</button>
                            </div>
                          ))}
                        </div>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <span style={{fontSize:'11px',color:'var(--muted)'}}>{folder.listCount} lists</span>
                          <div style={{display:'flex',gap:'6px'}}>
                            <button className="btn btn-sm" style={{padding:'3px 10px',fontSize:'11px'}} onClick={()=>navigate(`/phases/${folder.id}/dashboard`)}>📊 Dashboard</button>
                            {canEdit && <button className="btn btn-sm btn-primary" style={{padding:'3px 10px',fontSize:'11px'}} onClick={()=>{setSelectedFolder(folder);setShowListModal(true);}}>+ List</button>}
                            {/* Delete only for manage permission — hidden for view/edit */}
                            {!folder.readonly && (
                              <button className="btn btn-sm btn-danger" style={{padding:'3px 8px',fontSize:'11px'}} title="Delete Phase"
                                onClick={async()=>{
                                  setDeleteConfirm({
                                    msg: `Delete phase "${folder.name}" and ALL its lists and tasks? This cannot be undone.`,
                                    onConfirm: async () => {
                                      try { await api.delete(`/folders/${folder.id}`); load(); if(ctx?.reloadSidebar) ctx.reloadSidebar(spaceId); }
                                      catch(e){ console.error(e); }
                                    }
                                  });
                                }}>🗑</button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        )}

        {/* ── PERT TAB */}
        {activeTab==='pert' && (
          <div>
            <div style={{marginBottom:'16px'}}>
              <h3 style={{fontSize:'15px',fontWeight:'700',color:'var(--text)',marginBottom:'4px'}}>PERT Chart</h3>
              <p style={{fontSize:'12px',color:'var(--muted)'}}>Program Evaluation and Review Technique — task network with dependencies</p>
            </div>
            <div className="card" style={{padding:'20px'}}>
              <PERTChart tasks={data.pert?.tasks||[]} dependencies={data.pert?.dependencies||[]}/>
            </div>
          </div>
        )}

        {/* ── TIMESHEET TAB */}
        {activeTab==='timesheet' && (
          <div>
            <div style={{marginBottom:'16px'}}>
              <h3 style={{fontSize:'15px',fontWeight:'700',color:'var(--text)',marginBottom:'4px'}}>Time Overview</h3>
              <p style={{fontSize:'12px',color:'var(--muted)'}}>Estimated vs logged hours across all milestones</p>
            </div>

            {/* Summary cards */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'12px',marginBottom:'16px'}}>
              {[
                {label:'Team Logged Hours',value:`${Math.round((data.workload||[]).reduce((s,w)=>s+(w.total_hours||0),0))}h`,sub:'from active tasks',color:'#5B5BD6',icon:'⏱'},
                {label:'Avg Hours / Task',value:(data.stats?.totalTasks||0)>0?`${((data.workload||[]).reduce((s,w)=>s+(w.total_hours||0),0)/(data.stats?.totalTasks||1)).toFixed(1)}h`:'—',sub:'per task average',color:'#D97706',icon:'📊'},
                {label:'Active Members',value:(data.workload||[]).length,sub:'with assigned tasks',color:'#16A34A',icon:'👥'},
              ].map((c,i)=>(
                <div key={i} className="card" style={{padding:'16px 18px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}>
                    <span style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted)'}}>{c.label}</span>
                    <span style={{fontSize:'16px'}}>{c.icon}</span>
                  </div>
                  <div style={{fontSize:'26px',fontWeight:'800',color:c.color,letterSpacing:'-1px'}}>{c.value}</div>
                  <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'4px'}}>{c.sub}</div>
                </div>
              ))}
            </div>

            {/* Workload time bars */}
            <div className="card" style={{padding:'20px',marginBottom:'14px'}}>
              <div style={{fontSize:'12px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'14px'}}>Team Hours Distribution</div>
              {(data.workload||[]).length===0
                ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No workload data yet</div>
                : (data.workload||[]).map(w=>(
                    <div key={w.id} style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'12px'}}>
                      <div style={{width:28,height:28,borderRadius:'50%',background:w.avatar_color||'#5B5BD6',fontSize:'10px',color:'#fff',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'700'}}>
                        {w.full_name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'4px'}}>
                          <span style={{fontSize:'12px',fontWeight:'600',color:'var(--text)'}}>{w.full_name}</span>
                          <span style={{fontSize:'11px',color:'var(--muted)'}}>{w.active_count} tasks · <strong style={{color:'#5B5BD6'}}>{Math.round(w.total_hours||0)}h</strong></span>
                        </div>
                        <div style={{height:7,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                          <div style={{height:'100%',borderRadius:'99px',background:'#5B5BD6',width:`${Math.max(...(data.workload||[]).map(x=>x.total_hours||0),1)>0?((w.total_hours||0)/Math.max(...(data.workload||[]).map(x=>x.total_hours||0),1))*100:0}%`,transition:'width .5s ease'}}/>
                        </div>
                      </div>
                    </div>
                  ))
              }
            </div>

            <div style={{textAlign:'center'}}>
              <button className="btn btn-primary" onClick={()=>navigate(`/spaces/${spaceId}/timesheet`)}>
                📅 Open Full Timesheet
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showFolderModal&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'440px'}}>
            <h3 style={{fontSize:'16px',fontWeight:'700',marginBottom:'20px'}}>Create Phase</h3>
            <div className="form-group"><label className="form-label">Phase Name</label>
              <input className="input" value={newFolder.name} onChange={e=>setNewFolder({...newFolder,name:e.target.value})} placeholder="e.g. Phase 1 — Design, Execution…" autoFocus onKeyDown={e=>e.key==='Enter'&&createFolder()}/></div>
            <div className="form-group"><label className="form-label">Description</label>
              <input className="input" value={newFolder.description} onChange={e=>setNewFolder({...newFolder,description:e.target.value})} placeholder="Optional"/></div>
            <div className="form-group"><label className="form-label">Color</label>
              <div style={{display:'flex',gap:'8px'}}>{COLORS.map(c=><div key={c} onClick={()=>setNewFolder({...newFolder,color:c})} style={{width:26,height:26,borderRadius:'50%',background:c,cursor:'pointer',outline:newFolder.color===c?`3px solid ${c}`:'none',outlineOffset:'2px'}}/>)}</div></div>
            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',marginTop:'20px',paddingTop:'16px',borderTop:'1px solid var(--border)'}}>
              <button className="btn" onClick={()=>setShowFolderModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createFolder}>Create Phase</button>
            </div>
          </div>
        </div>
      )}

      {showListModal&&selectedFolder&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'440px'}}>
            <h3 style={{fontSize:'16px',fontWeight:'700',marginBottom:'6px'}}>Create List in "{selectedFolder.name}"</h3>
            <p style={{fontSize:'12px',color:'var(--muted)',marginBottom:'20px'}}>A milestone groups related tasks — e.g. Sprint 1, Deliverable A</p>
            <div className="form-group"><label className="form-label">List Name</label>
              <input className="input" value={newList.name} onChange={e=>setNewList({...newList,name:e.target.value})} placeholder="e.g. Sprint 1, Design Review…" autoFocus onKeyDown={e=>e.key==='Enter'&&createList()}/></div>
            <div className="form-group"><label className="form-label">Color</label>
              <div style={{display:'flex',gap:'8px'}}>{COLORS.map(c=><div key={c} onClick={()=>setNewList({...newList,color:c})} style={{width:26,height:26,borderRadius:'50%',background:c,cursor:'pointer',outline:newList.color===c?`3px solid ${c}`:'none',outlineOffset:'2px'}}/>)}</div></div>
            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',marginTop:'20px',paddingTop:'16px',borderTop:'1px solid var(--border)'}}>
              <button className="btn" onClick={()=>setShowListModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createList}>Create List</button>
            </div>
          </div>
        </div>
      )}

      {showPermModal&&(
        <PermissionPanel projectId={spaceId} allUsers={allUsers} onClose={()=>setShowPermModal(false)}/>
      )}

      {phasePermModal&&(
        <PhasePermissionPanel phaseId={phasePermModal} allUsers={allUsers} onClose={()=>setPhasePermModal(null)}/>
      )}

      {showWeeklyModal&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'460px',maxWidth:'94vw'}}>
            <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'6px'}}>
              <span style={{fontSize:'22px'}}>📅</span>
              <h3 style={{fontSize:'16px',fontWeight:'700',color:'var(--text)',margin:0}}>Weekly Summary — {space?.name}</h3>
            </div>
            <p style={{fontSize:'12px',color:'var(--muted)',marginBottom:'16px'}}>
              Email a progress snapshot of this project — with the Executive Report attached — to the project creator and its Project Managers on a schedule.
            </p>
            <label style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom:'1px solid var(--border)'}}>
              <span style={{fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>Enable weekly summary</span>
              <div onClick={()=>setWeeklyCfg(c=>({...c,enabled:!c.enabled}))}
                style={{width:44,height:24,borderRadius:12,background:weeklyCfg.enabled?'#5B5BD6':'#D1D5DB',display:'flex',alignItems:'center',padding:'2px',cursor:'pointer',transition:'background .2s'}}>
                <div style={{width:20,height:20,borderRadius:'50%',background:'#fff',transform:weeklyCfg.enabled?'translateX(20px)':'translateX(0)',transition:'transform .2s'}}/>
              </div>
            </label>
            {weeklyCfg.enabled && (
              <div style={{display:'flex',gap:'16px',marginTop:'16px',flexWrap:'wrap'}}>
                <div>
                  <label style={{display:'block',fontSize:'11px',fontWeight:'600',color:'var(--muted)',marginBottom:'4px'}}>Send on</label>
                  <select className="input" value={weeklyCfg.day} onChange={e=>setWeeklyCfg(c=>({...c,day:e.target.value}))} style={{height:'38px',minWidth:'140px'}}>
                    {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d,i)=><option key={i} value={String(i)}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{display:'block',fontSize:'11px',fontWeight:'600',color:'var(--muted)',marginBottom:'4px'}}>At time</label>
                  <select className="input" value={weeklyCfg.time} onChange={e=>setWeeklyCfg(c=>({...c,time:e.target.value}))} style={{height:'38px',minWidth:'110px'}}>
                    {Array.from({length:24},(_,h)=>['00','30'].map(m=>`${String(h).padStart(2,'0')}:${m}`)).flat().map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            )}
            {weeklyCfg.enabled && (
              <label style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'12px',padding:'14px 0 4px',marginTop:'8px',cursor:'pointer'}}>
                <div style={{paddingRight:'8px'}}>
                  <div style={{fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>Also send to every project member</div>
                  <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'2px'}}>Each member gets a personalized copy showing only the phases and lists they can access (and payments only if they're allowed to view them). The creator and PMs always get the full comprehensive report.</div>
                </div>
                <div onClick={()=>setWeeklyCfg(c=>({...c,perMember:!c.perMember}))}
                  style={{flexShrink:0,marginTop:'2px',width:44,height:24,borderRadius:12,background:weeklyCfg.perMember?'#5B5BD6':'#D1D5DB',display:'flex',alignItems:'center',padding:'2px',transition:'background .2s'}}>
                  <div style={{width:20,height:20,borderRadius:'50%',background:'#fff',transform:weeklyCfg.perMember?'translateX(20px)':'translateX(0)',transition:'transform .2s'}}/>
                </div>
              </label>
            )}
            {weeklyMsg && <div style={{fontSize:'12px',fontWeight:'600',color:weeklyMsg.toLowerCase().includes('fail')?'#DC2626':'#16A34A',marginTop:'14px'}}>{weeklyMsg}</div>}
            <div style={{display:'flex',gap:'8px',justifyContent:'space-between',alignItems:'center',marginTop:'20px',paddingTop:'14px',borderTop:'1px solid var(--border)'}}>
              <button className="btn" onClick={testWeekly} disabled={weeklyBusy} style={{fontWeight:'600'}}>✉️ Send test to creator</button>
              <div style={{display:'flex',gap:'8px'}}>
                <button className="btn" onClick={()=>setShowWeeklyModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={saveWeekly} disabled={weeklyBusy} style={{fontWeight:'700'}}>{weeklyBusy?'…':'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {weightAdjustModal&&(
        <WeightAdjustModal
          type={weightAdjustModal.type}
          newItemName={weightAdjustModal.newItemName}
          siblings={weightAdjustModal.siblings}
          usedWeight={weightAdjustModal.usedWeight}
          remaining={weightAdjustModal.remaining}
          onConfirm={weightAdjustModal.onConfirm}
          onCancel={weightAdjustModal.onCancel}
        />
      )}

      {/* Professional Delete Confirmation Modal */}
      {deleteConfirm&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'420px',textAlign:'center',padding:'32px'}}>
            <div style={{width:'56px',height:'56px',borderRadius:'50%',background:'rgba(220,38,38,0.1)',border:'2px solid rgba(220,38,38,0.2)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px',fontSize:'24px'}}>🗑</div>
            <h3 style={{fontSize:'16px',fontWeight:'700',color:'var(--text)',marginBottom:'10px'}}>Confirm Deletion</h3>
            <p style={{fontSize:'13px',color:'var(--muted)',lineHeight:1.6,marginBottom:'24px'}}>{deleteConfirm.msg}</p>
            <div style={{display:'flex',gap:'10px',justifyContent:'center'}}>
              <button className="btn" onClick={()=>setDeleteConfirm(null)}>Cancel</button>
              <button style={{padding:'8px 24px',background:'#DC2626',color:'#fff',border:'none',borderRadius:'var(--radius)',fontWeight:'700',cursor:'pointer',fontSize:'13px'}}
                onClick={()=>{ deleteConfirm.onConfirm(); setDeleteConfirm(null); }}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Smart Weight Adjustment Modal
// Shows when adding a new phase/list would require redistributing existing weights
function WeightAdjustModal({ type, newItemName, siblings, lockedSiblings, usedWeight, remaining, onConfirm, onCancel }) {
  const label = type === 'phase' ? 'phase' : 'list';
  const locked = lockedSiblings || []; // private phases — shown but not editable
  const lockedWeight = locked.reduce((s,l) => s+(l.weight||0), 0);
  const maxNew = remaining;

  // Initialize sibling adjustments
  const [sibs, setSibs] = useState(() =>
    siblings.map(s => ({ ...s }))
  );
  const [newWeight, setNewWeight] = useState(() => {
    // Start with an equal share for the new item
    const suggested = Math.max(1, Math.floor(100 / (siblings.length + 1)));
    return Math.min(remaining > 0 ? remaining : suggested, suggested);
  });

  // Auto-distribute siblings on first render so total = 100 immediately
  React.useEffect(() => {
    distributeProportionalForWeight(newWeight);
  // eslint-disable-next-line
  }, []);

  const sibTotal = sibs.reduce((s, x) => s + (x.weight || 0), 0);
  const grandTotal = sibTotal + newWeight + lockedWeight;
  const isValid = grandTotal <= 100 && newWeight > 0;

  function distributeProportionalForWeight(nw) {
    const target = Math.max(0, 100 - nw - lockedWeight); // locked phases are fixed
    if (siblings.length === 0) { setSibs([]); return; }
    const visibleUsed = siblings.reduce((s,x)=>s+(x.weight||0),0);
    if (visibleUsed === 0) {
      const each = Math.floor(target / siblings.length);
      setSibs(siblings.map((s,i)=>({...s, weight: i===siblings.length-1 ? target-each*(siblings.length-1) : each})));
      return;
    }
    const adjusted = siblings.map(s => ({
      ...s,
      weight: Math.max(0, Math.round((s.weight / visibleUsed) * target))
    }));
    const diff = target - adjusted.reduce((s, x) => s + x.weight, 0);
    if (adjusted.length > 0) adjusted[0].weight = Math.max(0, adjusted[0].weight + diff);
    setSibs(adjusted);
  }

  function distributeProportional() {
    distributeProportionalForWeight(newWeight);
  }

  function distributeEqual() {
    if (siblings.length === 0) return;
    const target = Math.max(0, 100 - newWeight - lockedWeight);
    const each = Math.floor(target / siblings.length);
    const adjusted = siblings.map((s, i) => ({
      ...s,
      weight: i === siblings.length - 1 ? target - each * (siblings.length - 1) : each
    }));
    setSibs(adjusted);
  }

  function updateSib(id, val) {
    setSibs(prev => prev.map(s => s.id === id ? { ...s, weight: Math.max(0, parseInt(val) || 0) } : s));
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width: '540px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(91,91,214,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>⚖️</div>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0 }}>Weight Distribution</h3>
            <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '2px 0 0' }}>
              Adding <strong>"{newItemName}"</strong> — adjust weights so they sum correctly
            </p>
          </div>
        </div>

        {/* Info */}
        <div style={{ background: 'rgba(91,91,214,0.06)', border: '1px solid rgba(91,91,214,0.2)', borderRadius: 'var(--radius-lg)', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#3730a3' }}>
          Existing {label}s use <strong>{usedWeight}%</strong> of the total 100%. Remaining available: <strong>{remaining}%</strong>.
          {locked.length > 0 && (
            <span style={{display:'block',marginTop:'6px',color:'#DC2626',fontWeight:'600'}}>
              ⚠️ {locked.length} private {label}{locked.length>1?'s':''} occupy <strong>{lockedWeight}%</strong> and cannot be adjusted here — only an admin can change their weight.
            </span>
          )}
          {remaining === 0 && locked.length > 0 && (
            <span style={{display:'block',marginTop:'6px',color:'#DC2626',fontWeight:'700'}}>
              The project is at 100% weight. You must reduce a visible {label}'s weight to make room for the new one.
            </span>
          )}
        </div>

        {/* New item weight */}
        <div className="form-group">
          <label className="form-label">New {label} weight: <strong style={{ color: 'var(--brand)' }}>"{newItemName}"</strong></label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input type="range" min="1" max="100" value={newWeight}
              onChange={e => { const nw=parseInt(e.target.value); setNewWeight(nw); distributeProportionalForWeight(nw); }}
              style={{ flex: 1, accentColor: 'var(--brand)' }} />
            <input type="number" min="1" max="100" value={newWeight}
              onChange={e => setNewWeight(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
              className="input" style={{ width: '65px', textAlign: 'center', fontWeight: '700' }} />
          </div>
        </div>

        {/* Redistribute buttons */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          <button className="btn btn-sm" onClick={distributeProportional}>⚖️ Proportional</button>
          <button className="btn btn-sm" onClick={distributeEqual}>═ Equal</button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            Total: <strong style={{ color: isValid ? '#16A34A' : '#DC2626', fontSize: '14px' }}>{grandTotal}</strong>/100
          </div>
        </div>

        {/* Existing items table */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ background: 'var(--surface2)', padding: '8px 14px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--muted)', display: 'grid', gridTemplateColumns: '1fr 80px 80px' }}>
            <span>Existing {label}</span><span style={{ textAlign: 'center' }}>Current</span><span style={{ textAlign: 'center' }}>New Weight</span>
          </div>
          {sibs.map((s, i) => (
            <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', borderTop: '1px solid var(--border)', alignItems: 'center', padding: '8px 14px', background: i % 2 === 0 ? 'transparent' : 'rgba(91,91,214,0.02)' }}>
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
              <span style={{ fontSize: '12px', color: 'var(--muted)', textAlign: 'center' }}>{siblings[i]?.weight || 0}%</span>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <input type="number" min="0" max="100" value={s.weight}
                  onChange={e => updateSib(s.id, e.target.value)}
                  style={{ width: '55px', height: '28px', border: `1.5px solid ${s.weight < (siblings[i]?.weight || 0) ? '#D97706' : 'var(--border)'}`, borderRadius: '6px', background: 'var(--surface)', color: 'var(--text)', textAlign: 'center', fontSize: '12px', fontWeight: '700', outline: 'none', padding: '0 4px' }} />
              </div>
            </div>
          ))}
          {/* New item row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', borderTop: '2px solid var(--brand)', alignItems: 'center', padding: '8px 14px', background: 'var(--brand-dim)' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--brand)' }}>+ {newItemName} <span style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: '400' }}>(new)</span></span>
            <span style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '12px' }}>—</span>
            <span style={{ textAlign: 'center', fontSize: '13px', fontWeight: '800', color: 'var(--brand)' }}>{newWeight}%</span>
          </div>
        </div>

        {/* Total progress bar */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12px' }}>
            <span style={{ color: 'var(--muted)' }}>Total weight</span>
            <strong style={{ color: isValid ? '#16A34A' : '#DC2626' }}>{grandTotal} / 100 {isValid ? '✓' : '⚠️'}</strong>
          </div>
          <div style={{ height: 8, background: 'var(--surface3)', borderRadius: '99px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, grandTotal)}%`, background: isValid ? '#16A34A' : '#DC2626', borderRadius: '99px', transition: 'width .3s' }} />
          </div>
          {!isValid && grandTotal > 100 && (
            <p style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>Total exceeds 100%. Reduce some weights to proceed.</p>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onConfirm(newWeight, sibs)} disabled={!isValid}>
            Confirm & Create
          </button>
        </div>
      </div>
    </div>
  );
}
